'use strict'
import React, { useReducer, useEffect, useCallback, useMemo, useState, useRef } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import {
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Toolbar,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  Tooltip,
  IconButton,
  Box,
  CircularProgress,
  Button,
  Link as MuiLink,
  Snackbar,
  Tabs,
  Tab,
  Drawer,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  Switch
} from '@material-ui/core'
import { Alert, Autocomplete } from '@material-ui/lab'
import { DFSearchInput, DFFilterChip, DFButton } from '../df'
import SearchIcon from '@material-ui/icons/Search'
import RefreshIcon from '@material-ui/icons/Refresh'
import SyncIcon from '@material-ui/icons/Sync'
import WarningIcon from '@material-ui/icons/Warning'
import ErrorIcon from '@material-ui/icons/Error'
import TrendingDownIcon from '@material-ui/icons/TrendingDown'
import TableChartIcon from '@material-ui/icons/TableChart'
import MapIcon from '@material-ui/icons/Map'
import EventNoteIcon from '@material-ui/icons/EventNote'
import LockIcon from '@material-ui/icons/Lock'
import CloseIcon from '@material-ui/icons/Close'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import EditIcon from '@material-ui/icons/Edit'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff'
import VisibilityIcon from '@material-ui/icons/Visibility'
import { useHistory, useLocation } from 'react-router-dom'
import {
  anonymizeProperty,
  anonymizeVisits,
  anonymizeIssues,
  anonymizeOpportunities
} from '../../utils/demoAnonymizer'
import { TerritoryProvider, useTerritory } from './TerritoryWorkbench/TerritoryContext'
import MapTab from './TerritoryWorkbench/MapTab'
import RoutesTab from './TerritoryWorkbench/RoutesTab'
import CREW_COLORS from './TerritoryWorkbench/crewColors'
import { HMI_YARD, haversineMiles, formatMinutes } from './TerritoryWorkbench/mapUtils'
import { DEFAULT_CURVE, wallClockMinutes } from './TerritoryWorkbench/crewEfficiency'
import { applyBasemapProvider, googleHybridProvider } from '../HerbicideSchedule/v3/maricopaBasemap'
import RoutingBreadcrumbs from './RoutingBreadcrumbs'
import { ThemeProvider } from '@material-ui/core/styles'
import desertFloraTheme from '../../styles/desertFloraTheme'

// Column definitions
const headCells = [
  { id: 'name', numeric: false, label: 'Property Name', width: 200 },
  { id: 'companyName', numeric: false, label: 'Company', width: 180 },
  { id: 'address1', numeric: false, label: 'Address', width: 180 },
  { id: 'completedTotalVisits', numeric: true, label: 'Visits M/T', width: 90 },
  { id: 'annualTotalVisits', numeric: true, label: 'Annual M/T', width: 90 },
  { id: 'estimatedHours', numeric: true, label: 'Est Hrs', width: 80 },
  { id: 'internalEstHoursDisplay', numeric: true, label: 'Internal Est', width: 100 },
  { id: 'avgOnSiteHoursDisplay', numeric: true, label: 'Avg On-Site', width: 100 },
  { id: 'medianOnSiteHoursDisplay', numeric: true, label: 'Med On-Site', width: 100 },
  { id: 'onSiteVariance', numeric: true, label: 'Est - Avg', width: 90 },
  { id: 'totalOTHours', numeric: true, label: 'OT Hrs', width: 80 },
  { id: 'estimatedLaborCost', numeric: true, label: 'Est Cost', width: 100 },
  { id: 'totalLaborCost', numeric: true, label: 'Actual Cost', width: 100 },
  { id: 'costVariance', numeric: true, label: 'Cost Var', width: 100 },
  { id: 'totalEstimatedRevenue', numeric: true, label: 'Est Rev', width: 100 },
  { id: 'totalEarnedRevenue', numeric: true, label: 'Earned Revenue', width: 120 },
  { id: 'netProfit', numeric: true, label: 'Net Profit', width: 100 },
  { id: 'profitMargin', numeric: true, label: 'Margin %', width: 90 }
]

const SYNC_ISSUE_LABELS = {
  'no-estimate-with-activity': 'Has visits but no internal estimate',
  'no-board-visits': 'Future tickets not on schedule board',
  'missing-geo': 'No valid coordinates',
  'no-crew-linkage': 'No crew size resolved from visits',
  'stale-not-processed': 'Not reached by last sync'
}

// Sorting helpers
function descendingComparator(a, b, orderBy) {
  const aVal = a[orderBy] ?? 0
  const bVal = b[orderBy] ?? 0
  if (bVal < aVal) return -1
  if (bVal > aVal) return 1
  return 0
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy)
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index])
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0])
    if (order !== 0) return order
    return a[1] - b[1]
  })
  return stabilizedThis.map((el) => el[0])
}

// Reducer for complex state management
const initialState = {
  rows: [],
  filteredRows: [],
  loading: true,
  error: null,
  order: 'asc',
  orderBy: 'name',
  page: 0,
  rowsPerPage: 20,
  searchText: '',
  companyFilter: '',
  // Quick filters
  showUnderBidOnly: false,
  showNegativeProfitOnly: false,
  showOvertimeOnly: false,
  showUnplacedOnly: false,
  showSyncProblemsOnly: false,
  // Sync status
  lastSyncedAt: null,
  syncing: false,
  // View mode (table or map)
  viewMode: 'table',
  // Demo mode (anonymizer)
  demoMode: false
}

function reducer(state, action) {
  switch (action.type) {
    case 'SET_DEMO_MODE':
      return { ...state, demoMode: action.payload }
    case 'SET_ROWS':
      return { ...state, rows: action.payload, filteredRows: action.payload, loading: false }
    case 'SET_FILTERED_ROWS':
      return { ...state, filteredRows: action.payload, page: 0 }
    case 'SET_LOADING':
      return { ...state, loading: action.payload }
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false }
    case 'SET_ORDER':
      return { ...state, order: action.payload.order, orderBy: action.payload.orderBy }
    case 'SET_PAGE':
      return { ...state, page: action.payload }
    case 'SET_ROWS_PER_PAGE':
      return { ...state, rowsPerPage: action.payload, page: 0 }
    case 'SET_SEARCH_TEXT':
      return { ...state, searchText: action.payload }
    case 'SET_COMPANY_FILTER':
      return { ...state, companyFilter: action.payload, page: 0 }
    case 'TOGGLE_UNDER_BID':
      return { ...state, showUnderBidOnly: !state.showUnderBidOnly }
    case 'TOGGLE_NEGATIVE_PROFIT':
      return { ...state, showNegativeProfitOnly: !state.showNegativeProfitOnly }
    case 'TOGGLE_OVERTIME':
      return { ...state, showOvertimeOnly: !state.showOvertimeOnly }
    case 'TOGGLE_UNPLACED':
      return { ...state, showUnplacedOnly: !state.showUnplacedOnly }
    case 'TOGGLE_SYNC_PROBLEMS':
      return { ...state, showSyncProblemsOnly: !state.showSyncProblemsOnly }
    case 'SET_SYNC_STATUS':
      return { ...state, lastSyncedAt: action.payload.lastSyncedAt }
    case 'SET_SYNCING':
      return { ...state, syncing: action.payload }
    case 'SET_VIEW_MODE':
      return { ...state, viewMode: action.payload }
    default:
      return state
  }
}

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    padding: theme.spacing(2),
    background: 'var(--df-bg, #f4efe4)'
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
    backgroundColor: 'var(--df-surface, #fbf8f1)',
    border: '1px solid var(--df-line, #e0d8c2)',
    borderRadius: 14,
    overflow: 'hidden'
  },
  toolbar: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    alignItems: 'center',
    background: 'var(--df-surface-2, #f6f1e3)',
    borderBottom: '1px solid var(--df-line, #e0d8c2)'
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: theme.spacing(1)
  },
  title: {
    marginRight: theme.spacing(2),
    fontWeight: 800,
    color: 'var(--df-ink, #1c2419)'
  },
  syncInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap'
  },
  searchFieldHalf: {
    flex: '1 1 0',
    minWidth: 240,
    display: 'flex'
  },
  companyFilter: {
    flex: '1 1 0',
    minWidth: 240,
    width: '100%',
    '& .MuiFormControl-root': {
      width: '100%'
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#ffffff',
      borderRadius: 10,
      paddingTop: 4,
      paddingBottom: 4,
      paddingLeft: 14,
      width: '100%'
    },
    '& .MuiOutlinedInput-input': {
      fontSize: 15,
      fontWeight: 500
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--df-line, #e0d8c2)',
      borderWidth: 1.5
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--df-line, #e0d8c2)'
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--df-primary, #1f5e3a)',
      borderWidth: 1.5
    }
  },
  refreshButton: {
    width: 40,
    height: 40,
    backgroundColor: 'var(--df-surface, #fbf8f1)',
    border: '1px solid var(--df-line, #e0d8c2)',
    color: 'var(--df-primary, #1f5e3a)',
    borderRadius: 10,
    '&:hover': {
      backgroundColor: 'var(--df-surface-2, #f6f1e3)'
    }
  },
  activeFilterChip: {
    height: 28,
    fontSize: 12,
    fontWeight: 600,
    backgroundColor: 'var(--df-primary-soft, #e3ecd8)',
    color: 'var(--df-primary, #1f5e3a)',
    borderColor: 'var(--df-primary, #1f5e3a)'
  },
  search: {
    padding: theme.spacing(1),
    minWidth: 280
  },
  filterChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  tableWrapper: {
    overflowX: 'auto'
  },
  table: {
    minWidth: 1680
  },
  tableCell: {
    padding: '10px 12px',
    fontSize: '0.85rem',
    borderColor: 'var(--df-line, #e0d8c2)'
  },
  clickableRow: {
    cursor: 'pointer',
    '&:focus': {
      outline: '2px solid var(--df-primary, #1f5e3a)',
      outlineOffset: -2
    }
  },
  headerCell: {
    fontWeight: 700,
    backgroundColor: 'var(--df-surface-2, #f6f1e3)',
    color: 'var(--df-ink-3, #6f7a6b)',
    whiteSpace: 'nowrap',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },
  negative: {
    color: 'var(--df-danger, #a9341d)',
    fontWeight: 700
  },
  positive: {
    color: 'var(--df-ok, #2f7a3e)',
    fontWeight: 700
  },
  warning: {
    color: 'var(--df-warn, #8c5b00)',
    fontWeight: 700
  },
  problemRow: {
    backgroundColor: 'var(--df-danger-soft, #f7dccf)'
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4)
  },
  statsWrapper: {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
    backgroundColor: 'var(--df-surface, #fbf8f1)',
    border: '1px solid var(--df-line, #e0d8c2)',
    borderRadius: 14,
    overflow: 'hidden',
    marginBottom: theme.spacing(2)
  },
  statsRow: {
    display: 'contents'
  },
  statTile: {
    position: 'relative',
    padding: '14px 16px 12px 18px',
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
    borderRight: '1px solid var(--df-line, #e0d8c2)',
    '&:last-child': {
      borderRight: 'none'
    }
  },
  statTileMoney: {
    backgroundColor: '#ffffff'
  },
  statAccent: {
    position: 'absolute',
    left: 0,
    top: 12,
    bottom: 12,
    width: 3,
    borderRadius: '0 2px 2px 0',
    backgroundColor: 'var(--df-ink-4, #b8b6a4)'
  },
  statAccentDanger: {
    backgroundColor: 'var(--df-danger, #a9341d)'
  },
  statAccentWarn: {
    backgroundColor: 'var(--df-warn, #8c5b00)'
  },
  statAccentPrimary: {
    backgroundColor: 'var(--df-primary, #1f5e3a)'
  },
  statAccentOk: {
    backgroundColor: 'var(--df-ok, #2f7a3e)'
  },
  statValue: {
    fontSize: '1.6rem',
    fontWeight: 800,
    color: 'var(--df-ink, #1c2419)',
    letterSpacing: '-0.02em',
    lineHeight: 1.1,
    fontVariantNumeric: 'tabular-nums'
  },
  statValueMoney: {
    fontSize: '1.5rem'
  },
  statLabel: {
    fontSize: '0.68rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: 'var(--df-ink-3, #6f7a6b)'
  },
  statHint: {
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--df-ink-3, #6f7a6b)',
    marginTop: 2,
    fontVariantNumeric: 'tabular-nums'
  },
  syncButton: {
    marginLeft: theme.spacing(1),
    backgroundColor: 'var(--df-primary, #1f5e3a)',
    color: '#fff',
    '&:hover': {
      backgroundColor: 'var(--df-primary-2, #164a2c)'
    }
  },
  lastSync: {
    fontSize: '0.75rem',
    color: 'var(--df-ink-3, #6f7a6b)'
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: 'center'
  },
  drawer: {
    width: 420,
    maxWidth: '100vw',
    backgroundColor: '#f4efe4',
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    color: '#1c2419',
    borderLeft: '1px solid #e7dfc9'
  },
  drawerHeader: {
    padding: '12px 16px 11px',
    backgroundColor: '#fbf8f1',
    borderBottom: '1px solid #e7dfc9'
  },
  drawerTitle: {
    fontWeight: 800,
    lineHeight: 1.2,
    fontSize: 16,
    color: '#1c2419'
  },
  drawerSubtitle: {
    marginTop: 2,
    fontSize: 11.5,
    color: '#6f7a6b',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  drawerContent: {
    padding: theme.spacing(1.5),
    overflowY: 'auto',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25)
  },
  drawerTabs: {
    display: 'flex',
    padding: '0 14px',
    background: '#fbf8f1',
    borderBottom: '1px solid #e7dfc9',
    gap: 14,
    overflowX: 'auto',
    flexShrink: 0
  },
  drawerTabButton: {
    padding: '9px 0 10px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    flexShrink: 0,
    fontFamily: 'inherit',
    fontSize: 12,
    fontWeight: 700,
    color: '#97a094',
    borderBottom: '2px solid transparent',
    marginBottom: -1,
    letterSpacing: 0,
    whiteSpace: 'nowrap'
  },
  drawerTabButtonActive: {
    color: '#1c2419',
    borderBottomColor: '#1f5e3a'
  },
  drawerKpiRibbon: {
    padding: '12px 12px 11px',
    background: '#fbf8f1',
    borderBottom: '1px solid #e7dfc9',
    flexShrink: 0
  },
  drawerKpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 6
  },
  drawerChipRow: {
    display: 'flex',
    gap: 5,
    marginTop: 9,
    flexWrap: 'wrap'
  },
  v4Chip: {
    fontSize: 10.5,
    fontWeight: 700,
    padding: '3px 9px',
    borderRadius: 99,
    background: '#f4efe4',
    border: '1px solid #e7dfc9',
    color: '#3d463a',
    display: 'inline-flex',
    alignItems: 'center',
    whiteSpace: 'nowrap'
  },
  v4GreenChip: {
    background: '#e3eee0',
    borderColor: '#c7dac8',
    color: '#164a2c'
  },
  v4MonoChip: {
    fontFamily: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
    letterSpacing: 0
  },
  v4AmberChip: {
    background: '#f7ecd4',
    borderColor: '#e3cf9b',
    color: '#8c5b00'
  },
  drawerScenarioNote: {
    marginTop: 9,
    padding: '7px 10px',
    borderRadius: 7,
    background: '#f7ecd4',
    border: '1px solid #e3cf9b',
    color: '#6e4a05',
    fontSize: 11,
    lineHeight: 1.45
  },
  drawerQuickStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 6,
    marginTop: 10
  },
  drawerQuickStat: {
    minWidth: 0,
    padding: '7px 8px',
    borderRadius: 7,
    background: '#faf6ee',
    border: '1px solid #ede6d2'
  },
  drawerQuickStatLabel: {
    fontSize: 8.8,
    fontWeight: 800,
    color: '#97a094',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  drawerQuickStatValue: {
    marginTop: 3,
    fontSize: 13,
    fontWeight: 800,
    color: '#1c2419',
    fontFamily: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
    whiteSpace: 'nowrap'
  },
  drawerQuickStatSub: {
    marginTop: 2,
    fontSize: 9.5,
    fontWeight: 600,
    color: '#6f7a6b',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  v4Card: {
    background: '#fbf8f1',
    border: '1px solid #e7dfc9',
    borderRadius: 10,
    padding: '11px 13px 13px'
  },
  v4CardTight: {
    padding: '10px 12px 11px'
  },
  v4CardHeader: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 6,
    marginBottom: 8,
    flexWrap: 'wrap'
  },
  v4CardTitle: {
    fontSize: 11,
    fontWeight: 800,
    color: '#3d463a',
    letterSpacing: 0,
    whiteSpace: 'nowrap'
  },
  v4CardSubtitle: {
    fontSize: 10.5,
    color: '#97a094',
    fontWeight: 600,
    whiteSpace: 'nowrap'
  },
  v4ActionLink: {
    marginLeft: 'auto',
    fontSize: 10.5,
    fontWeight: 700,
    color: '#1f5e3a',
    cursor: 'pointer',
    border: 'none',
    background: 'transparent',
    padding: 0,
    fontFamily: 'inherit'
  },
  drawerSection: {
    marginBottom: theme.spacing(2)
  },
  drawerSectionTitle: {
    fontSize: '0.75rem',
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: 'var(--df-ink-3, #6f7a6b)',
    marginBottom: theme.spacing(1)
  },
  drawerStatRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(0.75, 0),
    borderBottom: '1px solid var(--df-line, #e0d8c2)',
    fontSize: '0.875rem'
  },
  drawerStatLabel: {
    color: 'var(--df-ink-3, #6f7a6b)'
  },
  drawerStatValue: {
    color: 'var(--df-ink, #1c2419)',
    fontWeight: 700,
    textAlign: 'right'
  },
  serviceGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: theme.spacing(1.25)
  },
  servicePanel: {
    border: '1px solid var(--df-line, #e0d8c2)',
    borderRadius: 8,
    padding: theme.spacing(1.25),
    backgroundColor: 'var(--df-surface-2, #f6f1e3)'
  },
  servicePanelTitle: {
    fontSize: '0.85rem',
    fontWeight: 800,
    color: 'var(--df-ink, #1c2419)',
    marginBottom: theme.spacing(0.75)
  },
  drawerAlert: {
    padding: theme.spacing(1.25),
    borderRadius: 8,
    backgroundColor: 'var(--df-danger-soft, #f7dccf)',
    color: 'var(--df-danger, #a9341d)',
    fontSize: '0.85rem',
    fontWeight: 700,
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  drawerActions: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2)
  },
  visitRow: {
    padding: theme.spacing(1, 0),
    borderBottom: '1px solid var(--df-line, #e0d8c2)',
    fontSize: '0.85rem'
  },
  visitMeta: {
    color: 'var(--df-ink-3, #6f7a6b)',
    fontSize: '0.78rem',
    marginTop: 2
  },
  historyFilterRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(1.25)
  },
  clockEntryRow: {
    marginTop: theme.spacing(0.75),
    padding: theme.spacing(0.75),
    borderRadius: 6,
    backgroundColor: 'var(--df-bg-2, #f1ece0)',
    fontSize: '0.78rem'
  },
  miniRouteMap: {
    height: 170,
    border: '1px solid var(--df-line, #e0d8c2)',
    borderRadius: 8,
    background: 'linear-gradient(180deg, #eef2e9 0%, #f8f4e8 100%)',
    overflow: 'hidden',
    marginBottom: theme.spacing(1.25)
  },
  nearbySiteRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 0),
    borderBottom: '1px solid var(--df-line, #e0d8c2)',
    fontSize: '0.84rem'
  },
  emptyDrawerNote: {
    padding: theme.spacing(1.25),
    borderRadius: 8,
    backgroundColor: 'var(--df-bg-2, #f1ece0)',
    color: 'var(--df-ink-3, #6f7a6b)',
    fontSize: '0.82rem',
    lineHeight: 1.45
  }
}))

// Format currency
const formatCurrency = (value) => {
  if (value == null || isNaN(value)) return '-'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value)
}

// Format number with decimals
const formatNumber = (value, decimals = 2) => {
  if (value == null || isNaN(value)) return '-'
  return Number(value).toFixed(decimals)
}

const formatVisitSplit = (mtcVisits, totalVisits) => {
  const mtc = Number(mtcVisits || 0)
  const total = Number(totalVisits || 0)
  return total > 0 && total !== mtc ? `${mtc} / ${total}` : `${mtc}`
}

// Format percentage
const formatPercent = (value) => {
  if (value == null || isNaN(value)) return '-'
  return `${Number(value).toFixed(1)}%`
}

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return 'Never'
  const date = new Date(dateStr)
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

const formatShortDate = (dateStr) => {
  if (!dateStr) return '-'
  const parts = getAspireDateTimeParts(dateStr)
  if (parts) return formatDateParts(parts)
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return '-'
  return date.toLocaleDateString()
}

const formatShortDateTime = (dateStr) => {
  if (!dateStr) return '-'
  const parts = getAspireDateTimeParts(dateStr)
  if (parts) return parts.hour == null ? formatDateParts(parts) : `${formatDateParts(parts)}, ${formatTimeParts(parts)}`
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return String(dateStr)
  return date.toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })
}

const formatTimeOnly = (dateStr) => {
  if (!dateStr) return '-'
  const parts = getAspireDateTimeParts(dateStr)
  if (parts) return formatTimeParts(parts)
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return String(dateStr)
  return formatTimeParts({
    hour: date.getUTCHours(),
    minute: date.getUTCMinutes()
  })
}

const getAspireDateTimeParts = (value) => {
  if (!value) return null
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return null
    return {
      year: value.getUTCFullYear(),
      month: value.getUTCMonth() + 1,
      day: value.getUTCDate(),
      hour: value.getUTCHours(),
      minute: value.getUTCMinutes()
    }
  }

  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2}))?/)
  if (!match) return null
  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: match[4] == null ? null : Number(match[4]),
    minute: match[5] == null ? null : Number(match[5])
  }
}

const formatDateParts = ({ year, month, day }) => {
  if (!year || !month || !day) return '-'
  return new Date(year, month - 1, day).toLocaleDateString()
}

const formatTimeParts = ({ hour, minute }) => {
  if (hour == null || minute == null) return '-'
  const suffix = hour >= 12 ? 'PM' : 'AM'
  const displayHour = hour % 12 || 12
  return `${displayHour}:${String(minute).padStart(2, '0')} ${suffix}`
}

const getCrewLabel = (crew = {}) => {
  const selectedCrew = crew || {}
  return selectedCrew.name || selectedCrew.crewLeaderName || '-'
}

const getFirstPresent = (source = {}, keys = []) => {
  const row = source || {}
  for (const key of keys) {
    if (row[key] !== undefined && row[key] !== null && row[key] !== '') return row[key]
  }
  return null
}

const formatCurrencyOrPending = (value) => {
  if (value == null || value === '') return 'Pending data'
  return formatCurrency(value)
}

const getVisitDateValue = (visit = {}) => {
  return getFirstPresent(visit, [
    'date',
    'completedAt',
    'completedDate',
    'scheduledDate',
    'scheduledStartDate',
    'ScheduledStartDate',
    'ScheduledDate',
    'VisitDate',
    'ArrivalTime',
    'DepartureTime',
    'CompleteDate',
    'ApprovedDate',
    'startDate',
    'visitDate'
  ])
}

const formatVisitDateTime = (visit = {}) => {
  const dateValue = getVisitDateValue(visit)
  if (!dateValue) return 'Date pending'

  const date = new Date(dateValue)
  const dateParts = getAspireDateTimeParts(dateValue)
  const dateLabel = dateParts ? formatDateParts(dateParts) : isNaN(date.getTime()) ? String(dateValue) : date.toLocaleDateString()
  const startTime = getFirstPresent(visit, ['arrivalTime', 'ArrivalTime', 'startTime', 'StartTime', 'scheduledStartTime'])
  const endTime = getFirstPresent(visit, ['departureTime', 'DepartureTime', 'endTime', 'EndTime', 'scheduledEndTime'])

  if (startTime && endTime) return `${dateLabel} · ${formatTimeOnly(startTime)}-${formatTimeOnly(endTime)}`
  if (startTime) return `${dateLabel} · ${formatTimeOnly(startTime)}`
  if (dateParts && dateParts.hour != null) {
    return `${dateLabel}, ${formatTimeParts(dateParts)}`
  }
  if (!isNaN(date.getTime()) && (date.getUTCHours() || date.getUTCMinutes())) {
    return `${dateLabel}, ${formatTimeOnly(date)}`
  }
  return dateLabel
}

const getVisitCrewName = (visit = {}) => {
  return getFirstPresent(visit, [
    'CrewLeaderName',
    'crewLeaderName',
    'crewName',
    'foremanName',
    'assignedCrewName',
    'routeName',
    'RouteName',
    'PrimaryContactFullName',
    'ContactName'
  ]) || visit.crew?.name || 'Crew pending'
}

const getRawVisit = (visit = {}) => visit?.raw && typeof visit.raw === 'object' ? visit.raw : visit

const getWorkTicketId = (visit = {}) => {
  const row = getRawVisit(visit)
  return getFirstPresent(row, ['workTicketId', 'WorkTicketID']) || getFirstPresent(visit, ['workTicketId', 'WorkTicketID'])
}

const getWorkTicketNumber = (visit = {}) => {
  const row = getRawVisit(visit)
  return getFirstPresent(row, ['workTicketNumber', 'WorkTicketNumber']) || getFirstPresent(visit, ['ticket'])
}

const getWorkTicketVisitId = (visit = {}) => {
  const row = getRawVisit(visit)
  return getFirstPresent(row, [
    'workTicketVisitId',
    'workTicketVisitID',
    'WorkTicketVisitID',
    'WorkTicketVisitId',
    'VisitID',
    'visitId'
  ])
}

const getAspireWorkTicketUrl = (visit = {}) => {
  const workTicketId = getWorkTicketId(visit)
  return workTicketId ? `https://cloud.youraspire.com/app/worktickets/details/${workTicketId}` : null
}

const getAspireWorkTicketVisitUrl = (visit = {}) => {
  const workTicketVisitId = getWorkTicketVisitId(visit)
  return workTicketVisitId ? `https://cloud.youraspire.com/app/worktickets/visit/${workTicketVisitId}` : null
}

const getTicketLabel = (visit = {}) => {
  const ticketNumber = getWorkTicketNumber(visit)
  const ticketId = getWorkTicketId(visit)
  if (ticketNumber && ticketNumber !== '-') return `TX ${ticketNumber}`
  if (ticketId) return `Work Ticket ${ticketId}`
  return null
}

const getVisitCrewCountForNormalization = (visit = {}) => {
  const row = getRawVisit(visit)
  const entries = getCrewTimeEntries(visit)
  const distinctClockContacts = new Set(
    entries
      .filter(entry => !entry.isDriveTime)
      .map(entry => getFirstPresent(entry, ['contactId', 'ContactID', 'employeeId', 'EmployeeID']))
      .filter(Boolean)
  )
  if (distinctClockContacts.size > 1) return distinctClockContacts.size

  const actualCrewCount = safeNumber(getFirstPresent(row, ['actualCrewCount', 'ActualCrewCount']) || getFirstPresent(visit, ['actualCrewCount', 'ActualCrewCount']), 0)
  if (actualCrewCount > 0) return actualCrewCount

  const scheduledCrewCount = safeNumber(getFirstPresent(row, ['CrewMembers', 'crewMembers']) || getFirstPresent(visit, ['CrewMembers', 'crewMembers']), 0)
  if (scheduledCrewCount > 0 && scheduledCrewCount <= 2) return scheduledCrewCount

  return 3
}

const getCrewHourFallback = (visit = {}) => {
  const row = getRawVisit(visit)
  const onSiteCrewHours = safeNumber(getFirstPresent(row, [
    'actualOnSiteHours',
    'ActualOnSiteHours',
    'OnSiteHours',
    'TicketOnSiteHours'
  ]), 0)
  if (onSiteCrewHours <= 0) return 0

  const crewCount = getVisitCrewCountForNormalization(visit)
  return crewCount > 1 ? onSiteCrewHours / crewCount : onSiteCrewHours
}

const getVisitHours = (visit = {}) => {
  const clockDuration = getVisitClockDurationHours(visit)
  if (clockDuration > 0) return clockDuration

  const crewHourFallback = getCrewHourFallback(visit)
  if (crewHourFallback > 0) return crewHourFallback

  return getFirstPresent(visit, [
    'HoursAct',
    'Hours',
    'hours',
    'estimatedHours',
    'HoursEst'
  ])
}

const getVisitStatus = (visit = {}) => getFirstPresent(visit, [
  'WorkTicketStatusName',
  'WorkTicketStatus',
  'statusName',
  'status'
])

const getCrewTimeEntries = (visit = {}) => {
  const row = getRawVisit(visit)
  const entries = row.crewTimeEntries || row.CrewTimeEntries || visit.crewTimeEntries || []
  return Array.isArray(entries) ? entries : []
}

const getClockEntryName = (entry = {}) => {
  return getFirstPresent(entry, [
    'contactName',
    'ContactName',
    'crewMemberName',
    'CrewMemberName',
    'employeeName',
    'EmployeeName',
    'name',
    'Name'
  ]) || [entry.firstName || entry.FirstName, entry.lastName || entry.LastName].filter(Boolean).join(' ')
}

const getInitials = (name = '') => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

// Stable color picker so each crew member gets a consistent chip color
// across visits.
const PEOPLE_CHIP_COLORS = CREW_COLORS
const colorForName = (name = '') => {
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0
  return PEOPLE_CHIP_COLORS[Math.abs(hash) % PEOPLE_CHIP_COLORS.length]
}

// Aspire time-entry URL for a visit: jumps to the daily time-entry detail page
// for the work ticket on the visit's completed/scheduled date.
// Pattern: https://cloud.youraspire.com/app/timeentry/details/YYYY-MM-DD/0/0/<WorkTicketID>
const getAspireTimeEntryUrl = (visit = {}) => {
  const raw = getRawVisit(visit)
  const dateValue = getVisitDateValue(raw) || getVisitDateValue(visit)
  const ticketId = getWorkTicketId(raw) || getWorkTicketId(visit)
  if (!dateValue || !ticketId) return null
  // If the source already looks like a YYYY-MM-DD prefix, trust it verbatim —
  // Aspire-issued visit dates are calendar dates, not timezone-bearing instants,
  // so any timezone interpretation can shift them by a day.
  const dateStr = String(dateValue)
  const prefixMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/)
  let yyyy; let mm; let dd
  if (prefixMatch) {
    [, yyyy, mm, dd] = prefixMatch
  } else {
    const date = new Date(dateStr)
    if (isNaN(date.getTime())) return null
    yyyy = date.getUTCFullYear()
    mm = String(date.getUTCMonth() + 1).padStart(2, '0')
    dd = String(date.getUTCDate()).padStart(2, '0')
  }
  return `https://cloud.youraspire.com/app/timeentry/details/${yyyy}-${mm}-${dd}/0/0/${ticketId}`
}

const getVisitPeopleChips = (visit = {}) => {
  const names = getCrewTimeEntries(visit)
    .map(getClockEntryName)
    .filter(Boolean)
  const uniqueNames = [...new Set(names)]
  if (!uniqueNames.length) {
    const fallback = visit.crew || getVisitCrewName(getRawVisit(visit))
    return { chips: [], fallback: fallback || null, overflow: 0 }
  }
  // Show every clocked-in crew member by default. The legacy "+N" overflow is
  // kept as a guardrail for pathological visits with very large crews.
  const SHOW = 12
  const chips = uniqueNames.slice(0, SHOW).map(name => ({
    name,
    initials: getInitials(name),
    color: colorForName(name)
  }))
  return { chips, fallback: null, overflow: Math.max(0, uniqueNames.length - SHOW) }
}

const getVisitTimeRange = (visit = {}) => {
  const row = getRawVisit(visit)
  const start = getFirstPresent(row, ['arrivalTime', 'ArrivalTime', 'startTime', 'StartTime', 'scheduledStartTime'])
  const end = getFirstPresent(row, ['departureTime', 'DepartureTime', 'endTime', 'EndTime', 'scheduledEndTime'])
  if (start || end) return `${start ? formatTimeOnly(start) : '-'}-${end ? formatTimeOnly(end) : '-'}`

  const entries = getCrewTimeEntries(visit)
  const starts = entries.map(entry => getFirstPresent(entry, ['startTime', 'StartTime'])).filter(Boolean)
  const ends = entries.map(entry => getFirstPresent(entry, ['endTime', 'EndTime'])).filter(Boolean)
  if (!starts.length && !ends.length) return null

  const startDates = starts.map(value => new Date(value)).filter(date => !isNaN(date.getTime()))
  const endDates = ends.map(value => new Date(value)).filter(date => !isNaN(date.getTime()))
  const firstStart = startDates.length ? new Date(Math.min(...startDates.map(date => date.getTime()))) : starts[0]
  const lastEnd = endDates.length ? new Date(Math.max(...endDates.map(date => date.getTime()))) : ends[ends.length - 1]
  return `${firstStart ? formatTimeOnly(firstStart) : '-'}-${lastEnd ? formatTimeOnly(lastEnd) : '-'}`
}

const getTimestampMs = (value) => {
  if (!value) return null
  const date = value instanceof Date ? value : new Date(value)
  return isNaN(date.getTime()) ? null : date.getTime()
}

const getDurationHours = (start, end) => {
  const startMs = getTimestampMs(start)
  const endMs = getTimestampMs(end)
  if (startMs == null || endMs == null || endMs <= startMs) return null
  return (endMs - startMs) / (60 * 60 * 1000)
}

const getEntryHours = (entry = {}) => safeNumber(getFirstPresent(entry, ['hours', 'Hours', 'TotalHours']), 0)

const averagePositive = (values = []) => {
  const positive = values.map(value => safeNumber(value, 0)).filter(value => value > 0)
  if (!positive.length) return 0
  return positive.reduce((sum, value) => sum + value, 0) / positive.length
}

const getVisitClockDurationHours = (visit = {}) => {
  const row = getRawVisit(visit)
  const start = getFirstPresent(row, ['arrivalTime', 'ArrivalTime', 'startTime', 'StartTime', 'scheduledStartTime'])
  const end = getFirstPresent(row, ['departureTime', 'DepartureTime', 'endTime', 'EndTime', 'scheduledEndTime'])
  const directDuration = getDurationHours(start, end)
  if (directDuration) return directDuration

  const onSiteEntries = getCrewTimeEntries(visit).filter(entry => !entry.isDriveTime)
  const starts = onSiteEntries.map(entry => getTimestampMs(getFirstPresent(entry, ['startTime', 'StartTime']))).filter(value => value != null)
  const ends = onSiteEntries.map(entry => getTimestampMs(getFirstPresent(entry, ['endTime', 'EndTime']))).filter(value => value != null)
  if (starts.length && ends.length) {
    const entryDuration = (Math.max(...ends) - Math.min(...starts)) / (60 * 60 * 1000)
    if (entryDuration > 0) return entryDuration
  }

  return averagePositive(onSiteEntries.map(getEntryHours))
}

// Associated drive time for a visit: HoursAct - OnSiteHours, as reported by
// Aspire. Returns 0 when either field is missing or the math goes negative
// (clock skew, partial timecards, etc.).
const getVisitAssociatedDriveHours = (visit = {}) => {
  const row = getRawVisit(visit)
  const hoursAct = safeNumber(getFirstPresent(row, ['HoursAct', 'hoursAct']) ?? getFirstPresent(visit, ['HoursAct', 'hoursAct']), 0)
  const onSite = safeNumber(getFirstPresent(row, ['OnSiteHours', 'onSiteHours', 'ActualOnSiteHours', 'actualOnSiteHours', 'TicketOnSiteHours']) ?? getFirstPresent(visit, ['OnSiteHours', 'onSiteHours', 'ActualOnSiteHours', 'actualOnSiteHours', 'TicketOnSiteHours']), 0)
  if (hoursAct <= 0 || onSite <= 0) return 0
  return Math.max(0, hoursAct - onSite)
}

const getVisitDriveDurationHours = (visit = {}) => {
  const driveEntries = getCrewTimeEntries(visit).filter(entry => entry.isDriveTime)
  if (!driveEntries.length) return 0

  const starts = driveEntries.map(entry => getTimestampMs(getFirstPresent(entry, ['startTime', 'StartTime']))).filter(value => value != null)
  const ends = driveEntries.map(entry => getTimestampMs(getFirstPresent(entry, ['endTime', 'EndTime']))).filter(value => value != null)
  if (starts.length && ends.length) {
    const entryDuration = (Math.max(...ends) - Math.min(...starts)) / (60 * 60 * 1000)
    if (entryDuration > 0) return entryDuration
  }

  return averagePositive(driveEntries.map(getEntryHours))
}

const getMedian = (values = []) => {
  const sorted = values.map(value => safeNumber(value, 0)).filter(value => value > 0).sort((a, b) => a - b)
  if (!sorted.length) return 0
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2
}

const filterVisitRowsByType = (visits = [], typeFilter = 'all') => {
  if (!typeFilter || typeFilter === 'all') return visits
  return visits.filter(visit => visit.type === typeFilter)
}

const getVisitTrendStats = (visits = [], fallbackAvg = 0) => {
  const recentVisits = visits.filter(visit => visit.wAgo < 4)
  const priorVisits = visits.filter(visit => visit.wAgo >= 4 && visit.wAgo < 12)
  const recentAvg = recentVisits.length
    ? recentVisits.reduce((sum, visit) => sum + visit.hrs, 0) / recentVisits.length
    : fallbackAvg
  const priorAvg = priorVisits.length
    ? priorVisits.reduce((sum, visit) => sum + visit.hrs, 0) / priorVisits.length
    : recentAvg

  return {
    recentAvg,
    trendPct: priorAvg ? Math.round(((recentAvg - priorAvg) / priorAvg) * 100) : 0
  }
}

const HISTORY_RANGE_OPTIONS = [
  { value: '30', label: 'Past 30' },
  { value: '60', label: 'Past 60' },
  { value: '90', label: 'Past 90' },
  { value: '180', label: 'Past 180' },
  { value: 'year', label: 'Year' },
  { value: 'forever', label: 'Forever' }
]

const PROPERTY_DETAIL_TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'history', label: 'History' },
  { value: 'service', label: 'Service' },
  { value: 'schedule', label: 'Schedule' },
  { value: 'financials', label: 'Financials' }
]

const V4_T = {
  cream: '#f4efe4',
  surface: '#fbf8f1',
  surface2: '#faf6ee',
  line: '#e7dfc9',
  lineSoft: '#ede6d2',
  ink: '#1c2419',
  ink2: '#3d463a',
  mute: '#6f7a6b',
  muteSoft: '#97a094',
  green: '#1f5e3a',
  greenSoft: '#e3eee0',
  greenSofter: '#eef5eb',
  rust: '#a9341d',
  rustSoft: '#fbeee6',
  orange: '#c66a45',
  amber: '#d8973a',
  plum: '#6e3fb7',
  blue: '#3454c1',
  mono: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace'
}

const SERVICE_COLORS = {
  Maint: V4_T.orange,
  Maintenance: V4_T.orange,
  Irrig: V4_T.green,
  Irrigation: V4_T.green,
  Herb: V4_T.plum,
  Herbicide: V4_T.plum,
  Other: V4_T.mute
}

const clamp = (value, min, max) => Math.min(max, Math.max(min, value))

const safeNumber = (value, fallback = 0) => {
  const number = Number(value)
  return Number.isFinite(number) ? number : fallback
}

const pickNumber = (source = {}, keys = [], fallback = 0) => {
  const value = getFirstPresent(source, keys)
  return value === null ? fallback : safeNumber(value, fallback)
}

const formatCompactCurrency = (value) => {
  const number = safeNumber(value)
  if (Math.abs(number) >= 1000) return `$${(number / 1000).toFixed(1)}k`
  return formatCurrency(number)
}

const getDayAbbrev = (day) => {
  const value = String(day || '').trim()
  if (!value) return '-'
  return value.slice(0, 3).toUpperCase()
}

const getServiceTypeKey = (visit = {}) => {
  const service = String(
    getFirstPresent(visit, ['serviceName', 'ServiceName', 'ServiceDisplayName', 'serviceCategory', 'ServiceCategory']) || ''
  ).toLowerCase()
  if (service.includes('irrig')) return 'Irrig'
  if (service.includes('herb')) return 'Herb'
  if (service.includes('maint')) return 'Maint'
  return 'Other'
}

const getVisitDate = (visit = {}) => {
  const value = getVisitDateValue(visit)
  const date = value ? new Date(value) : null
  return date && !isNaN(date.getTime()) ? date : null
}

const getWeeksAgo = (date, now = new Date()) => {
  if (!date) return 0
  return Math.max(0, Math.round((now.getTime() - date.getTime()) / (7 * 24 * 60 * 60 * 1000)))
}

const buildVisitChartRows = (selectedProperty, detailVisits = []) => {
  const sourceVisits = detailVisits.length ? detailVisits : getVisitHistoryRows(selectedProperty)
  const now = new Date()
  const mapped = sourceVisits
    .map((visit, index) => {
      const hrs = safeNumber(getVisitHours(visit), 0)
      const date = getVisitDate(visit)
      const dateValue = getVisitDateValue(visit)
      return {
        id: getWorkTicketId(visit) || getWorkTicketNumber(visit) || `${date?.toISOString() || 'visit'}-${index}`,
        wAgo: getWeeksAgo(date, now),
        hrs,
        type: getServiceTypeKey(visit),
        crew: getVisitCrewName(visit),
        ticket: getWorkTicketNumber(visit) || getWorkTicketId(visit) || '-',
        date: dateValue ? formatShortDate(dateValue) : formatVisitDateTime(visit),
        status: getVisitStatus(visit),
        flag: hrs > safeNumber(selectedProperty?.estimatedHours, 0) && safeNumber(selectedProperty?.estimatedHours, 0) > 0 ? 'high' : null,
        raw: visit
      }
    })
    .filter(row => row.hrs > 0)

  // Dedupe by work-ticket id: server payload sometimes has one entry per
  // crewTimeEntry/WorkTicketTime row, which surfaces as duplicate visit rows
  // here. Keep the entry with the most actual hours per ticket; on a tie,
  // prefer the more specific service type (anything but "Other").
  const TYPE_PRIORITY = { Maint: 3, Irrig: 3, Herb: 3, Other: 0 }
  const byTicket = new Map()
  for (const row of mapped) {
    const key = row.id
    const incumbent = byTicket.get(key)
    if (!incumbent) { byTicket.set(key, row); continue }
    const incHrs = incumbent.hrs
    const newHrs = row.hrs
    if (newHrs > incHrs) byTicket.set(key, row)
    else if (newHrs === incHrs && (TYPE_PRIORITY[row.type] || 0) > (TYPE_PRIORITY[incumbent.type] || 0)) {
      byTicket.set(key, row)
    }
  }
  const rows = Array.from(byTicket.values()).sort((a, b) => a.wAgo - b.wAgo)

  if (rows.length) return rows

  const avgHours = safeNumber(selectedProperty?.avgOnSiteHoursDisplay || selectedProperty?.avgOnSiteHours)
  return Array.from({ length: Math.min(safeNumber(selectedProperty?.completedOccurrences, 12), 12) || 6 }, (_, index) => ({
    id: `fallback-${index}`,
    wAgo: index,
    hrs: avgHours || safeNumber(selectedProperty?.estimatedHours, 1),
    type: index % 5 === 0 ? 'Irrig' : 'Maint',
    crew: getCrewLabel(selectedProperty?.primaryCrewLeader),
    ticket: '-',
    date: index === 0 ? 'Recent' : `${index}w ago`,
    status: 'Complete',
    flag: null,
    raw: {}
  }))
}

const buildMonthlyRows = (selectedProperty, visitRows = []) => {
  const monthRows = []
  const now = new Date()
  const ticketRows = Array.isArray(selectedProperty?.workTickets) ? selectedProperty.workTickets : []
  const hasTicketFinancials = ticketRows.some(ticket =>
    getFirstPresent(ticket, ['EarnedRevenue', 'earnedRevenue', 'LaborCostAct', 'laborCostAct']) !== null
  )
  const fallbackRevenue = pickNumber(selectedProperty, ['maintenanceEarnedRevenue', 'totalEarnedRevenue', 'totalRevenue'])
  const fallbackLaborCost = pickNumber(selectedProperty, ['maintenanceLaborCost', 'totalLaborCost'])

  for (let index = 11; index >= 0; index -= 1) {
    const monthDate = new Date(now.getFullYear(), now.getMonth() - index, 1)
    const key = `${monthDate.getFullYear()}-${monthDate.getMonth()}`
    const visits = visitRows.filter(visit => {
      const date = getVisitDate(visit.raw)
      return date && `${date.getFullYear()}-${date.getMonth()}` === key
    })
    const tickets = hasTicketFinancials
      ? ticketRows.filter(ticket => {
          const date = getVisitDate(ticket)
          return date && `${date.getFullYear()}-${date.getMonth()}` === key
        })
      : []
    const visitCount = visits.length
    const actualHours = visitCount
      ? visits.reduce((sum, visit) => sum + visit.hrs, 0) / visitCount
      : safeNumber(selectedProperty?.avgOnSiteHoursDisplay || selectedProperty?.avgOnSiteHours)
    const monthlyRevenue = hasTicketFinancials
      ? tickets.reduce((sum, ticket) => sum + pickNumber(ticket, ['EarnedRevenue', 'earnedRevenue']), 0)
      : fallbackRevenue / 12
    const monthlyCost = hasTicketFinancials
      ? tickets.reduce((sum, ticket) => sum + pickNumber(ticket, ['LaborCostAct', 'laborCostAct']), 0)
      : fallbackLaborCost / 12
    monthRows.push({
      key,
      short: monthDate.toLocaleString([], { month: 'short' }).slice(0, 1),
      label: monthDate.toLocaleString([], { month: 'short' }),
      est: safeNumber(selectedProperty?.estimatedHours),
      act: actualHours,
      rev: monthlyRevenue,
      othRev: 0,
      cost: monthlyCost,
      visits: visitCount || tickets.length || (!hasTicketFinancials ? Math.max(0, Math.round(safeNumber(selectedProperty?.completedOccurrences) / 12)) : 0),
      current: index === 0
    })
  }

  return monthRows
}

const buildServiceRows = (selectedProperty = {}) => {
  const irrigation = getServiceDetail(selectedProperty, 'Irrigation')
  const herbicide = getServiceDetail(selectedProperty, 'Herbicide')
  const maintenanceCompleted = Math.max(0, safeNumber(selectedProperty.completedOccurrences) - irrigation.completed - herbicide.completed)
  const maintenanceFuture = Math.max(0, safeNumber(selectedProperty.futureTicketCount || selectedProperty.futureVisits?.length) - irrigation.future - herbicide.future)
  const maintenanceHours = Math.max(0, safeNumber(selectedProperty.totalOnSiteHours) - safeNumber(irrigation.hours) - safeNumber(herbicide.hours))
  const maintenanceCost = Math.max(0, safeNumber(selectedProperty.totalLaborCost) - safeNumber(irrigation.cost) - safeNumber(herbicide.cost))
  const rows = [
    {
      name: 'Irrigation',
      tickets: irrigation.tickets,
      completed: irrigation.completed,
      future: irrigation.future,
      hours: safeNumber(irrigation.hours),
      cost: irrigation.cost
    },
    {
      name: 'Herbicide',
      tickets: herbicide.tickets,
      completed: herbicide.completed,
      future: herbicide.future,
      hours: safeNumber(herbicide.hours),
      cost: herbicide.cost
    },
    {
      name: 'Maintenance',
      tickets: maintenanceCompleted + maintenanceFuture,
      completed: maintenanceCompleted,
      future: maintenanceFuture,
      hours: maintenanceHours,
      cost: maintenanceCost
    }
  ].filter(service => service.tickets > 0 || service.hours > 0)

  const totalHours = rows.reduce((sum, service) => sum + service.hours, 0) || 1
  return rows.map(service => ({
    ...service,
    share: service.hours / totalHours
  }))
}

const getPrimaryCrewName = (property = {}) => {
  return getCrewLabel(property.primaryCrewLeader) !== '-'
    ? getCrewLabel(property.primaryCrewLeader)
    : getCrewLabel(property.assignedCrewLeaders?.[0] || property.crewLeaders?.[0])
}

const buildSidebarData = (selectedProperty, detailHistory, geoInsights) => {
  const property = selectedProperty || {}
  const visitRows = buildVisitChartRows(property, detailHistory.visits || [])
  const monthlyRows = buildMonthlyRows(property, visitRows)
  const serviceRows = buildServiceRows(property)
  const completedVisits = safeNumber(property.completedOccurrences)
  const annualVisits = safeNumber(property.targetAnnualVisits || property.annualOccurrences)
  const futureVisits = safeNumber(property.futureVisits?.length || property.futureTicketCount)
  const normalizedVisitHours = visitRows.map(visit => visit.hrs).filter(hours => hours > 0)
  const normalizedAvgHours = averagePositive(normalizedVisitHours)
  const normalizedMedianHours = getMedian(normalizedVisitHours)
  const fallbackMedianHours = safeNumber(property.medianOnSiteHoursDisplay || property.medianOnSiteHours)
  const avgHours = normalizedAvgHours || safeNumber(property.avgOnSiteHoursDisplay || property.avgOnSiteHours)
  const estHours = safeNumber(property.estimatedHours)
  // History-backed internal estimate (man-hour baseline ÷ median crew) —
  // preferred for display over the Aspire contract figure when present.
  const internalEstHours = safeNumber(property.internalEstimatedHours)
  const internalEstSource = property.internalEstimateSource || null
  const hasInternalEst = internalEstHours > 0 && internalEstSource && internalEstSource !== 'aspire'
  const normalizedDriveHours = averagePositive(visitRows.map(getVisitDriveDurationHours))
  const rolledUpTotalActualHours = safeNumber(property.avgActualHours || property.hoursActual || property.avgOnSiteHoursDisplay || property.avgOnSiteHours)
  const rolledUpDriveActual = Math.max(0, rolledUpTotalActualHours - safeNumber(property.avgOnSiteHoursDisplay || property.avgOnSiteHours))
  const driveActual = normalizedDriveHours || rolledUpDriveActual
  const totalActualHours = avgHours + driveActual
  const driveExpected = safeNumber(property.driveExpectedHours, geoInsights?.yardRoute?.durationMinutes ? geoInsights.yardRoute.durationMinutes / 60 : safeNumber(geoInsights?.yardDriveMinutes) / 60)
  const totalEarnedRev = pickNumber(property, ['totalEarnedRevenue', 'totalRevenue'])
  const maintenanceEarnedRev = getFirstPresent(property, ['maintenanceEarnedRevenue']) === null
    ? totalEarnedRev
    : pickNumber(property, ['maintenanceEarnedRevenue'])
  const otherEarnedRev = pickNumber(property, ['otherEarnedRevenue'])
  const totalRev = getFirstPresent(property, ['totalEarnedRevenue', 'totalRevenue']) === null
    ? maintenanceEarnedRev + otherEarnedRev
    : totalEarnedRev
  const estLabor = pickNumber(property, ['estimatedLaborCost'])
  const actualLabor = pickNumber(property, ['maintenanceLaborCost', 'totalLaborCost'])
  const costVariance = getFirstPresent(property, ['costVariance']) === null
    ? estLabor - actualLabor
    : pickNumber(property, ['costVariance'])
  const netProfit = getFirstPresent(property, ['netProfit']) === null
    ? maintenanceEarnedRev - actualLabor
    : pickNumber(property, ['netProfit'])
  const crewName = getPrimaryCrewName(property)
  const scheduledDay = property.scheduledDays?.[0] || property.futureVisits?.[0]?.dayOfWeek || property.day || 'Unscheduled'

  return {
    property,
    propertyId: getAspirePropertyId(property),
    aspireUrl: getAspirePropertyUrl(property),
    address: [property.address1, property.address2].filter(Boolean).join(' '),
    crewName,
    scheduledDay,
    // Scenario context (set when the drawer is opened from the refine planner):
    // parked = scenario-assigned to a crew outside the active maintenance pool;
    // scenarioUnscheduled = on the scenario route but not placed on a day.
    parkedCrewName: property.parkedCrewName || null,
    parkedCrewInactive: property.parkedCrewInactive ?? null,
    parkedCrewDivision: property.parkedCrewDivision || null,
    scenarioUnscheduled: !!property.scenarioUnscheduled,
    companyChip: property.companyId ? `${property.companyName || 'Company'} #${property.companyId}` : null,
    visitRows,
    monthlyRows,
    serviceRows,
    kpis: {
      margin: safeNumber(property.profitMargin),
      netProfit,
      earnedRev: maintenanceEarnedRev,
      othRev: otherEarnedRev,
      totalRev,
      estLabor,
      actualLabor,
      costVariance,
      estHoursPerVisit: estHours,
      internalEstHours,
      internalEstSource,
      internalEstVisitCount: safeNumber(property.internalEstimateVisitCount),
      hasInternalEst,
      avgHours,
      medianHours: normalizedMedianHours || fallbackMedianHours,
      normalizedMedianHours,
      normalizedVisitCount: normalizedVisitHours.length,
      fallbackMedianHours,
      estVariance: estHours - avgHours,
      otHours: safeNumber(property.totalOTHours),
      completedVisits,
      annualVisits,
      futureVisits,
      hoursActual: totalActualHours,
      driveActual,
      driveExpected
    }
  }
}

const getPropertyCoordinates = (row = {}) => {
  const property = row || {}
  const lat = Number(property.latitude ?? property.location?.coordinates?.[1])
  const lng = Number(property.longitude ?? property.location?.coordinates?.[0])
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  if (lat === 0 && lng === 0) return null
  return { lat, lng }
}

const formatMiles = (miles) => {
  const value = Number(miles)
  if (!Number.isFinite(value)) return '-'
  return `${value.toFixed(value < 10 ? 1 : 0)} mi`
}

const estimateRoadMiles = (straightLineMiles) => {
  const miles = Number(straightLineMiles)
  if (!Number.isFinite(miles)) return null
  return miles <= 0 ? 0 : miles * 1.25
}

const estimateDriveMinutes = (roadMiles) => {
  const miles = Number(roadMiles)
  if (!Number.isFinite(miles)) return null
  if (miles <= 0) return 0
  return Math.max(4, Math.round((miles / 32) * 60))
}

const buildGeoInsights = (selectedProperty, allRows = []) => {
  const selectedCoords = getPropertyCoordinates(selectedProperty)
  if (!selectedCoords) {
    return {
      hasCoordinates: false,
      selectedCoords: null,
      nearestSite: null,
      sitesWithinFiveMiles: [],
      sitesWithinThreeMiles: [],
      sitesWithinOneMile: [],
      avgDistanceWithinFiveMiles: null,
      medianDistanceWithinFiveMiles: null,
      yardStraightMiles: null,
      yardRoadMiles: null,
      yardDriveMinutes: null
    }
  }

  const selectedId = String(getAspirePropertyId(selectedProperty) || selectedProperty?._id || '')
  const nearbySites = allRows
    .map(row => {
      const coords = getPropertyCoordinates(row)
      const rowId = String(getAspirePropertyId(row) || row?._id || '')
      if (!coords || (selectedId && rowId === selectedId)) return null
      const distanceMiles = haversineMiles(selectedCoords.lat, selectedCoords.lng, coords.lat, coords.lng)
      return { ...row, distanceMiles }
    })
    .filter(Boolean)
    .sort((a, b) => a.distanceMiles - b.distanceMiles)

  const yardStraightMiles = haversineMiles(HMI_YARD.lat, HMI_YARD.lng, selectedCoords.lat, selectedCoords.lng)
  const yardRoadMiles = estimateRoadMiles(yardStraightMiles)
  const sitesWithinFiveMiles = nearbySites.filter(site => site.distanceMiles <= 5)
  const withinFiveDistances = sitesWithinFiveMiles.map(site => site.distanceMiles).sort((a, b) => a - b)
  const avgDistanceWithinFiveMiles = withinFiveDistances.length
    ? withinFiveDistances.reduce((sum, distance) => sum + distance, 0) / withinFiveDistances.length
    : null
  const medianDistanceWithinFiveMiles = withinFiveDistances.length
    ? withinFiveDistances[Math.floor(withinFiveDistances.length / 2)]
    : null

  return {
    hasCoordinates: true,
    selectedCoords,
    nearestSite: nearbySites[0] || null,
    sitesWithinFiveMiles,
    sitesWithinThreeMiles: nearbySites.filter(site => site.distanceMiles <= 3),
    sitesWithinOneMile: nearbySites.filter(site => site.distanceMiles <= 1),
    avgDistanceWithinFiveMiles,
    medianDistanceWithinFiveMiles,
    yardStraightMiles,
    yardRoadMiles,
    yardDriveMinutes: estimateDriveMinutes(yardRoadMiles)
  }
}

function MiniRouteMap ({ property, geoInsights, classes }) {
  const mapContainerRef = useRef(null)
  const mapRef = useRef(null)
  const tileLayerRef = useRef(null)
  const routeLayerRef = useRef(null)

  const siteCoords = geoInsights?.selectedCoords
  const yardRoute = geoInsights?.yardRoute
  const siteLat = siteCoords?.lat
  const siteLng = siteCoords?.lng
  const nearby = (geoInsights?.sitesWithinFiveMiles || []).slice(0, 8)
  const nearbyKey = nearby
    .map(site => `${getAspirePropertyId(site) || site._id || site.name}:${Number(site.distanceMiles || 0).toFixed(3)}`)
    .join('|')
  const routeKey = yardRoute?.geometry?.coordinates?.length
    ? `${yardRoute.geometry.coordinates.length}:${Number(yardRoute.distanceMiles || 0).toFixed(3)}:${Number(yardRoute.durationMinutes || 0).toFixed(2)}`
    : 'no-route'
  const propertyLabel = property?.name || 'Property'

  useEffect(() => {
    if (!mapContainerRef.current || !Number.isFinite(siteLat) || !Number.isFinite(siteLng)) return
    if (mapRef.current) {
      try {
        mapRef.current.stop()
        mapRef.current.off()
        mapRef.current.remove()
      } catch (_) {}
      mapRef.current = null
    }

    const map = L.map(mapContainerRef.current, {
      center: [siteLat, siteLng],
      zoom: 11,
      zoomControl: true,
      attributionControl: false,
      dragging: true,
      scrollWheelZoom: false,
      fadeAnimation: false,
      zoomAnimation: false,
      markerZoomAnimation: false
    })
    mapRef.current = map
    applyBasemapProvider({ map, tileLayerRef, provider: googleHybridProvider })

    const bounds = [[HMI_YARD.lat, HMI_YARD.lng], [siteLat, siteLng]]

    L.circleMarker([HMI_YARD.lat, HMI_YARD.lng], {
      radius: 8,
      color: '#fff',
      weight: 2,
      fillColor: '#1c2419',
      fillOpacity: 1
    }).bindTooltip('HMI Yard').addTo(map)

    L.circleMarker([siteLat, siteLng], {
      radius: 9,
      color: '#fff',
      weight: 3,
      fillColor: '#a9341d',
      fillOpacity: 1
    }).bindTooltip(propertyLabel).addTo(map)

    nearby.forEach(site => {
      const coords = getPropertyCoordinates(site)
      if (!coords) return
      bounds.push([coords.lat, coords.lng])
      L.circleMarker([coords.lat, coords.lng], {
        radius: 4,
        color: '#fff',
        weight: 1,
        fillColor: '#3454c1',
        fillOpacity: 0.85
      }).bindTooltip(`${site.name || 'Nearby site'} (${formatMiles(site.distanceMiles)})`).addTo(map)
    })

    const routeCoords = yardRoute?.geometry?.coordinates || []
    if (routeCoords.length >= 2) {
      const latlngs = routeCoords.map(([lng, lat]) => [lat, lng])
      routeLayerRef.current = L.polyline(latlngs, {
        color: '#1f5e3a',
        weight: 5,
        opacity: 0.9
      }).addTo(map)
      latlngs.forEach(latlng => bounds.push(latlng))
    }

    map.fitBounds(bounds, { padding: [22, 22], maxZoom: 15 })
    setTimeout(() => {
      if (mapRef.current === map) map.invalidateSize()
    }, 80)

    return () => {
      if (mapRef.current) {
        try {
          mapRef.current.stop()
          mapRef.current.off()
          mapRef.current.remove()
        } catch (_) {}
        mapRef.current = null
      }
      routeLayerRef.current = null
      tileLayerRef.current = null
    }
  }, [siteLat, siteLng, routeKey, nearbyKey, propertyLabel])

  if (!siteCoords) {
    return (
      <div className={classes.emptyDrawerNote}>
        No coordinates available for a map preview.
      </div>
    )
  }

  return (
    <>
      <div className={classes.miniRouteMap}>
        <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />
      </div>
      {geoInsights.routeLoading && (
        <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 8 }}>
          Loading road route...
        </Typography>
      )}
      {geoInsights.routeError && (
        <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 8 }}>
          Road geometry unavailable: {geoInsights.routeError}
        </Typography>
      )}
    </>
  )
}

const getVisitHistoryRows = (property = {}) => {
  const row = property || {}
  return getFirstPresent(row, [
    'visitHistory',
    'completedVisits',
    'recentVisits',
    'pastVisits',
    'visits'
  ]) || []
}

const getServiceDetail = (property = {}, service) => {
  const prefix = service.toLowerCase()
  const displayName = service === 'Irrigation' ? 'Irrigation' : 'Herbicide'
  const completed = Number(property?.[`${prefix}CompletedVisits`] || 0)
  const future = Number(property?.[`${prefix}FutureVisits`] || 0)
  const hours = property?.[`${prefix}Hours`]
  const cost = getFirstPresent(property, [
    `${prefix}Cost`,
    `${prefix}LaborCost`,
    `${prefix}TotalCost`,
    `${prefix}TotalLaborCost`,
    `total${displayName}Cost`,
    `total${displayName}LaborCost`
  ])

  return {
    name: displayName,
    tickets: completed + future,
    completed,
    future,
    hours,
    cost
  }
}

const normalizeRoutingRow = (row = {}) => {
  const estimatedHours = Number(row.estimatedHours || 0)
  const internalEstHoursDisplay = Number(row.internalEstimatedHours || 0)
  const avgOnSiteHoursDisplay = Number(row.avgOnSiteHours || row.avgActualHours || 0)
  const medianOnSiteHoursDisplay = Number(row.medianOnSiteHours || row.medianActualHours || 0)
  const companyId = row.companyId || row.CompanyID || null
  const companyName = row.companyName || row.CompanyName || ''
  const totalEarnedRevenue = pickNumber(row, ['totalEarnedRevenue', 'totalRevenue'])
  const contractPricePerVisit = Number(row.contractPricePerVisit || 0)
  const annualTicketCount = Number(row.annualOccurrences || 0)
  const targetAnnualVisits = Number(row.targetAnnualVisits || annualTicketCount || 0)
  const annualVisitsDisplay = targetAnnualVisits || annualTicketCount
  const completedMtcVisits = Number(row.completedOccurrences || 0)
  const herbicideCompletedVisits = Number(row.herbicideCompletedVisits || 0)
  const irrigationCompletedVisits = Number(row.irrigationCompletedVisits || 0)
  const herbicideAnnualVisits = herbicideCompletedVisits + Number(row.herbicideFutureVisits || 0)
  const irrigationAnnualVisits = irrigationCompletedVisits + Number(row.irrigationFutureVisits || 0)
  const completedTotalVisits = completedMtcVisits + herbicideCompletedVisits + irrigationCompletedVisits
  const annualTotalVisits = annualVisitsDisplay + herbicideAnnualVisits + irrigationAnnualVisits
  const estimatedRevenueFromPlan = contractPricePerVisit > 0 && targetAnnualVisits > 0 ? contractPricePerVisit * targetAnnualVisits : 0
  const totalEstimatedRevenue = Number(estimatedRevenueFromPlan || row.totalEstimatedRevenue || 0)
  
  const maintenanceEarnedRevenue = getFirstPresent(row, ['maintenanceEarnedRevenue']) === null
    ? totalEarnedRevenue
    : pickNumber(row, ['maintenanceEarnedRevenue'])
  const maintenanceLaborCost = pickNumber(row, ['maintenanceLaborCost', 'totalLaborCost'])
  const netProfit = getFirstPresent(row, ['netProfit']) === null
    ? maintenanceEarnedRevenue - maintenanceLaborCost
    : pickNumber(row, ['netProfit'])

  return {
    ...row,
    companyId,
    companyName,
    companyLabel: companyId
      ? `${companyName || 'Company'} #${companyId}`
      : 'No company',
    estimatedHours,
    internalEstHoursDisplay,
    avgOnSiteHoursDisplay,
    medianOnSiteHoursDisplay,
    onSiteVariance: estimatedHours - avgOnSiteHoursDisplay,
    completedMtcVisits,
    completedTotalVisits,
    herbicideCompletedVisits,
    irrigationCompletedVisits,
    herbicideAnnualVisits,
    irrigationAnnualVisits,
    annualTicketCount,
    targetAnnualVisits,
    annualVisitsDisplay,
    annualTotalVisits,
    estimatedRevenueFromPlan,
    contractPricePerVisit,
    totalEstimatedRevenue,
    maintenanceEarnedRevenue,
    maintenanceLaborCost,
    netProfit,
    totalEarnedRevenue
  }
}

const getAspirePropertyId = (row = {}) => {
  const property = row || {}
  return property.aspirePropertyId || property.propertyId || property.PropertyID || null
}

const getAspirePropertyUrl = (row = {}) => {
  const propertyId = getAspirePropertyId(row)
  return propertyId ? `https://cloud.youraspire.com/app/properties/overview/${propertyId}` : null
}

function DetailCard ({ classes, title, subtitle, action, onAction, right, children, tight = false }) {
  return (
    <div className={`${classes.v4Card} ${tight ? classes.v4CardTight : ''}`}>
      <div className={classes.v4CardHeader} style={{ marginBottom: tight ? 0 : 8 }}>
        <span className={classes.v4CardTitle}>{title}</span>
        {subtitle && <span className={classes.v4CardSubtitle}>{subtitle}</span>}
        {right && <span style={{ marginLeft: 'auto' }}>{right}</span>}
        {action && !right && (
          <button className={classes.v4ActionLink} onClick={onAction} type="button">
            {action} →
          </button>
        )}
      </div>
      {children}
    </div>
  )
}

function SegmentedControl ({ value, options, onChange }) {
  return (
    <div style={{ display: 'flex', background: V4_T.cream, borderRadius: 6, padding: 2, gap: 1, border: `1px solid ${V4_T.line}` }}>
      {options.map(option => (
        <button
          key={option.value}
          onClick={() => onChange(option.value)}
          type="button"
          style={{
            padding: '3px 9px',
            borderRadius: 4,
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: 10,
            fontWeight: 700,
            background: value === option.value ? V4_T.surface : 'transparent',
            color: value === option.value ? V4_T.ink : V4_T.muteSoft,
            boxShadow: value === option.value ? '0 1px 2px rgba(28,36,25,0.08)' : 'none'
          }}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

function Sparkline ({ values, width = 88, height = 22, color = V4_T.green, fill = true }) {
  const cleanValues = values.length ? values.map(value => safeNumber(value)) : [0, 0]
  const yMin = Math.min(...cleanValues)
  const yMax = Math.max(...cleanValues)
  const range = yMax - yMin || 1
  const xStep = cleanValues.length > 1 ? (width - 2) / (cleanValues.length - 1) : width - 2
  const points = cleanValues.map((value, index) => [
    1 + index * xStep,
    height - 2 - ((value - yMin) / range) * (height - 4)
  ])
  const path = points.map((point, index) => `${index ? 'L' : 'M'}${point[0].toFixed(1)} ${point[1].toFixed(1)}`).join(' ')
  const fillPath = `${path} L ${points[points.length - 1][0].toFixed(1)} ${height - 1} L ${points[0][0].toFixed(1)} ${height - 1} Z`

  return (
    <svg width={width} height={height} style={{ display: 'block', maxWidth: '100%' }}>
      {fill && <path d={fillPath} fill={color} opacity="0.12" />}
      <path d={path} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={points[points.length - 1][0]} cy={points[points.length - 1][1]} r="2" fill={color} stroke={V4_T.surface} strokeWidth="1" />
    </svg>
  )
}

function RibbonKPI ({ label, value, unit, tone = 'ink', spark, color = V4_T.green }) {
  const valueColor = tone === 'bad' ? V4_T.rust : tone === 'good' ? V4_T.green : V4_T.ink
  return (
    <div style={{ padding: '9px 10px 10px', borderRadius: 8, background: V4_T.cream, border: `1px solid ${V4_T.line}`, minWidth: 0 }}>
      <div style={{ fontSize: 9, fontWeight: 800, color: V4_T.muteSoft, textTransform: 'uppercase', letterSpacing: 0 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 2, marginTop: 3 }}>
        <span style={{ fontSize: 18, fontWeight: 800, color: valueColor, lineHeight: 1 }}>{value}</span>
        {unit && <span style={{ fontSize: 11, fontWeight: 700, color: V4_T.muteSoft }}>{unit}</span>}
      </div>
      {spark && <div style={{ marginTop: 5 }}><Sparkline values={spark} color={color} width={104} height={20} /></div>}
    </div>
  )
}

function OvertimeKPI ({ value, visits }) {
  const bars = visits.slice(-18)
  const maxHours = Math.max(1, ...bars.map(visit => visit.hrs))
  return (
    <div style={{ padding: '9px 10px 10px', borderRadius: 8, background: V4_T.cream, border: `1px solid ${V4_T.line}`, minWidth: 0 }}>
      <div style={{ fontSize: 9, fontWeight: 800, color: V4_T.muteSoft, textTransform: 'uppercase', letterSpacing: 0 }}>Overtime</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 2, marginTop: 3 }}>
        <span style={{ fontSize: 18, fontWeight: 800, color: V4_T.ink, lineHeight: 1 }}>{formatNumber(value, 1)}</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: V4_T.muteSoft }}>h</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 20, marginTop: 5 }}>
        {(bars.length ? bars : [{ hrs: 0 }]).map((visit, index) => (
          <div
            key={`${visit.id || 'bar'}-${index}`}
            style={{
              flex: 1,
              height: Math.max(3, (visit.hrs / maxHours) * 18),
              borderRadius: 1,
              background: visit.flag === 'high' ? V4_T.rust : V4_T.green,
              opacity: visit.flag === 'high' ? 0.95 : 0.5
            }}
          />
        ))}
      </div>
    </div>
  )
}

function HoursDotPlot ({ visits, est, onHover, onSelect, onOpen, width = 368, height = 122 }) {
  const rows = visits.length ? visits : [{ wAgo: 0, hrs: 0, type: 'Other' }]
  const padX = 8
  const padTop = 14
  const padBot = 22
  const weekMax = Math.max(1, ...rows.map(visit => visit.wAgo)) + 1
  const yMax = Math.max(1, est * 1.05, ...rows.map(visit => visit.hrs * 1.05))
  const xToPx = (weeksAgo) => padX + (1 - weeksAgo / weekMax) * (width - padX * 2)
  const yToPx = (hours) => padTop + (1 - hours / yMax) * (height - padTop - padBot)

  return (
    <svg width="100%" viewBox={`0 0 ${width} ${height}`} style={{ display: 'block' }}>
      <defs>
        <pattern id="routing-detail-dotgrid" width="14" height="14" patternUnits="userSpaceOnUse">
          <circle cx="0.6" cy="0.6" r="0.6" fill={V4_T.line} />
        </pattern>
      </defs>
      <rect x={padX} y={padTop} width={width - padX * 2} height={height - padTop - padBot} fill="url(#routing-detail-dotgrid)" opacity="0.5" />
      <line x1={padX} x2={width - padX} y1={yToPx(est)} y2={yToPx(est)} stroke={V4_T.rust} strokeWidth="1" strokeDasharray="3 3" opacity="0.55" />
      <text x={width - padX - 2} y={yToPx(est) - 4} textAnchor="end" fontSize="9.5" fontWeight="700" fill={V4_T.rust} fontFamily={V4_T.mono}>EST {formatNumber(est, 1)}h</text>
      <line x1={padX} x2={width - padX} y1={yToPx(0)} y2={yToPx(0)} stroke={V4_T.line} strokeWidth="1" />
      {rows.map((visit, index) => {
        const x = xToPx(visit.wAgo)
        const y = yToPx(visit.hrs)
        const color = SERVICE_COLORS[visit.type] || V4_T.mute
        const previewVisit = () => onHover && onHover(visit)
        const selectVisit = () => onSelect ? onSelect(visit) : previewVisit()
        const openVisit = () => onOpen && onOpen(visit)
        return (
          <g
            key={`${visit.id || 'visit'}-${index}`}
            role="button"
            tabIndex="0"
            onMouseEnter={previewVisit}
            onClick={selectVisit}
            onDoubleClick={openVisit}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                selectVisit()
              } else if (event.key === 'o' || event.key === 'O') {
                openVisit()
              }
            }}
            style={{ cursor: 'pointer', outline: 'none' }}
          >
            <line x1={x} x2={x} y1={yToPx(0)} y2={y} stroke={color} strokeWidth="1" opacity="0.18" />
            <circle cx={x} cy={y} r={visit.flag === 'high' ? 4.5 : 3.5} fill={color} stroke={V4_T.surface} strokeWidth="1.4" opacity="0.92" />
            {visit.flag === 'high' && <circle cx={x} cy={y} r="7" fill="none" stroke={color} strokeWidth="1" opacity="0.35" />}
          </g>
        )
      })}
      {[weekMax - 1, Math.floor(weekMax / 2), 0].map((weeksAgo, index) => (
        <text key={weeksAgo} x={xToPx(weeksAgo)} y={height - 6} textAnchor={index === 0 ? 'start' : index === 2 ? 'end' : 'middle'} fontSize="9.5" fill={V4_T.muteSoft} fontWeight="600" fontFamily={V4_T.mono}>
          {weeksAgo === 0 ? 'NOW' : weeksAgo === Math.floor(weekMax / 2) ? `${weeksAgo}W` : `${weeksAgo}W AGO`}
        </text>
      ))}
    </svg>
  )
}

function MonthlyHoursBars ({ monthly, est, width = 368, height = 104 }) {
  const padX = 4
  const padTop = 12
  const padBot = 18
  const yMax = Math.max(1, est * 1.1, ...monthly.map(month => month.act)) * 1.05
  const innerW = width - padX * 2
  const innerH = height - padTop - padBot
  const colW = innerW / Math.max(monthly.length, 1)
  const yToPx = (hours) => padTop + (1 - hours / yMax) * innerH

  return (
    <svg width="100%" viewBox={`0 0 ${width} ${height}`} style={{ display: 'block' }}>
      <line x1={padX} x2={width - padX} y1={yToPx(0)} y2={yToPx(0)} stroke={V4_T.line} strokeWidth="1" />
      <line x1={padX} x2={width - padX} y1={yToPx(est)} y2={yToPx(est)} stroke={V4_T.rust} strokeWidth="1" strokeDasharray="2 3" opacity="0.5" />
      {monthly.map((month, index) => {
        const x = padX + index * colW + colW * 0.18
        const barW = colW * 0.64
        const yActual = yToPx(month.act)
        const over = month.act > est
        return (
          <g key={month.key || index}>
            <rect x={x} y={yActual} width={barW} height={yToPx(0) - yActual} rx="2" fill={over ? V4_T.rust : V4_T.green} opacity={month.current ? 1 : 0.85} />
            <text x={x + barW / 2} y={height - 5} textAnchor="middle" fontSize="9.5" fontWeight={month.current ? 800 : 600} fill={month.current ? V4_T.ink : V4_T.muteSoft} fontFamily={V4_T.mono}>{month.short}</text>
          </g>
        )
      })}
    </svg>
  )
}

function RevCostBars ({ monthly, width = 368, height = 96 }) {
  const padX = 4
  const padTop = 8
  const padBot = 16
  const yMax = Math.max(1, ...monthly.map(month => Math.max(month.rev + month.othRev, month.cost))) * 1.1
  const innerW = width - padX * 2
  const innerH = height - padTop - padBot
  const colW = innerW / Math.max(monthly.length, 1)
  const yToPx = (value) => padTop + (1 - value / yMax) * innerH

  return (
    <svg width="100%" viewBox={`0 0 ${width} ${height}`} style={{ display: 'block' }}>
      <line x1={padX} x2={width - padX} y1={yToPx(0)} y2={yToPx(0)} stroke={V4_T.line} strokeWidth="1" />
      {monthly.map((month, index) => {
        const center = padX + index * colW + colW / 2
        const totalRev = month.rev + month.othRev
        return (
          <g key={month.key || index}>
            <rect x={center - 6} y={yToPx(month.rev)} width="5" height={yToPx(0) - yToPx(month.rev)} fill={V4_T.green} opacity="0.88" rx="1" />
            {month.othRev > 0 && <rect x={center - 6} y={yToPx(totalRev)} width="5" height={yToPx(month.rev) - yToPx(totalRev)} fill={V4_T.amber} opacity="0.92" rx="1" />}
            <rect x={center + 1} y={yToPx(month.cost)} width="5" height={yToPx(0) - yToPx(month.cost)} fill={V4_T.mute} opacity="0.75" rx="1" />
            <text x={center} y={height - 4} textAnchor="middle" fontSize="9" fontWeight={month.current ? 700 : 500} fill={month.current ? V4_T.ink : V4_T.muteSoft} fontFamily={V4_T.mono}>{month.short}</text>
          </g>
        )
      })}
    </svg>
  )
}

function ServiceBars ({ services }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {services.map(service => {
        const color = SERVICE_COLORS[service.name] || V4_T.mute
        return (
          <div key={service.name}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 3 }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: color, flexShrink: 0 }} />
              <span style={{ fontSize: 11.5, fontWeight: 600, color: V4_T.ink2, flex: 1 }}>{service.name}</span>
              <span style={{ fontSize: 10.5, color: V4_T.muteSoft, fontFamily: V4_T.mono, fontWeight: 600 }}>{service.completed}/{service.tickets} tx</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: V4_T.ink, fontFamily: V4_T.mono, minWidth: 42, textAlign: 'right' }}>{formatNumber(service.hours, 1)}h</span>
            </div>
            <div style={{ height: 4, background: V4_T.line, borderRadius: 2, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${clamp(service.share * 100, 0, 100)}%`, background: color, borderRadius: 2 }} />
            </div>
          </div>
        )
      })}
    </div>
  )
}

function DrawerQuickStat ({ classes, label, value, sub, tone }) {
  const valueColor = tone === 'bad' ? V4_T.rust : tone === 'good' ? V4_T.green : tone === 'warn' ? '#a87320' : V4_T.ink
  return (
    <div className={classes.drawerQuickStat}>
      <div className={classes.drawerQuickStatLabel}>{label}</div>
      <div className={classes.drawerQuickStatValue} style={{ color: valueColor }}>{value}</div>
      {sub && <div className={classes.drawerQuickStatSub}>{sub}</div>}
    </div>
  )
}

// Frequency options shared by the maintenance / irrigation / herbicide selects.
// Value 'none' represents "not serviced" so the field is never left ambiguous.
const PROPERTY_FREQUENCY_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'semiannual', label: 'Semi-Annually' },
  { value: 'annual', label: 'Annually' },
  { value: 'asneeded', label: 'As Needed' }
]

// Edit-property modal. UI-only for now — captures fields into local state and
// reports them via onSave; persistence is wired up in a later pass.
function PropertyEditModal ({ open, onClose, property, onSave, crews }) {
  const [prescribedManHours, setPrescribedManHours] = useState('')
  const [mtcFrequency, setMtcFrequency] = useState('none')
  const [irrigationFrequency, setIrrigationFrequency] = useState('none')
  const [herbicideFrequency, setHerbicideFrequency] = useState('none')
  const [mowingRequired, setMowingRequired] = useState(false)
  const [lockToCrew, setLockToCrew] = useState(null)
  const [irrigationTech, setIrrigationTech] = useState(null)
  const [noIrrigation, setNoIrrigation] = useState(false)

  // Seed the form from the property each time the modal opens.
  useEffect(() => {
    if (!open) return
    const p = property || {}
    const crewList = crews || []
    setPrescribedManHours(p.prescribedManHours != null ? String(p.prescribedManHours) : '')
    setMtcFrequency(p.mtcFrequency || 'none')
    setIrrigationFrequency(p.irrigationCheckFrequency || 'none')
    setHerbicideFrequency(p.herbicideApplicationFrequency || 'none')
    setMowingRequired(!!p.mowingRequired)
    setLockToCrew(p.lockToCrewId ? (crewList.find(c => c._id === p.lockToCrewId) || null) : null)
    setIrrigationTech(p.irrigationTechId ? (crewList.find(c => c._id === p.irrigationTechId) || null) : null)
    setNoIrrigation(!!p.noIrrigation)
  }, [open, property])

  const handleSave = () => {
    const payload = {
      prescribedManHours: prescribedManHours === '' ? null : Number(prescribedManHours),
      mtcFrequency,
      irrigationCheckFrequency: irrigationFrequency,
      herbicideApplicationFrequency: herbicideFrequency,
      mowingRequired,
      lockToCrewId: lockToCrew?._id || null,
      irrigationTechId: noIrrigation ? null : (irrigationTech?._id || null),
      noIrrigation
    }
    if (onSave) onSave(payload)
    onClose()
  }

  const labelStyle = { fontSize: 12, fontWeight: 600, color: V4_T.ink2, marginBottom: 4, display: 'block' }
  const fieldWrap = { marginBottom: 16 }

  const renderFrequency = (label, value, setValue) => (
    <div style={fieldWrap}>
      <span style={labelStyle}>{label}</span>
      <FormControl fullWidth size="small" variant="outlined">
        <Select value={value} onChange={(e) => setValue(e.target.value)}>
          {PROPERTY_FREQUENCY_OPTIONS.map(opt => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  )

  const getCrewOptionLabel = (c) => c?.name || c?.foremanName || ''
  const getCrewOptionSelected = (o, v) => o?._id === v?._id

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle style={{ borderBottom: `1px solid ${V4_T.line}`, paddingBottom: 8 }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: V4_T.ink }}>Edit Property</span>
          <span style={{ fontSize: 12, color: V4_T.muteSoft, fontFamily: V4_T.mono, marginTop: 2 }}>
            {property?.name || 'Property'}
          </span>
        </div>
      </DialogTitle>
      <DialogContent style={{ paddingTop: 16 }}>
        <div style={fieldWrap}>
          <span style={labelStyle}>Prescribed Man Hours</span>
          <TextField
            type="number"
            fullWidth
            size="small"
            variant="outlined"
            value={prescribedManHours}
            onChange={(e) => setPrescribedManHours(e.target.value)}
            placeholder="e.g. 9"
            inputProps={{ min: 0, step: 0.25 }}
            helperText="How long we want the crew on site (man-hours)."
          />
        </div>

        {renderFrequency('Maintenance Frequency', mtcFrequency, setMtcFrequency)}
        {renderFrequency('Irrigation Check Frequency', irrigationFrequency, setIrrigationFrequency)}
        {renderFrequency('Herbicide Application Frequency', herbicideFrequency, setHerbicideFrequency)}

        <div style={fieldWrap}>
          <span style={labelStyle}>Lock to Crew</span>
          <Autocomplete
            options={crews || []}
            getOptionLabel={getCrewOptionLabel}
            getOptionSelected={getCrewOptionSelected}
            value={lockToCrew}
            onChange={(_, val) => setLockToCrew(val)}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" size="small" placeholder="Any crew" />
            )}
          />
        </div>

        <div style={{ ...fieldWrap, marginBottom: 4 }}>
          <FormControlLabel
            control={
              <Switch
                checked={noIrrigation}
                onChange={(e) => {
                  setNoIrrigation(e.target.checked)
                  if (e.target.checked) setIrrigationTech(null)
                }}
                color="primary"
              />
            }
            label={
              <span style={{ fontSize: 13, fontWeight: 600, color: V4_T.ink2 }}>
                {noIrrigation ? 'Property Has No Irrigation' : 'Property Has Irrigation (Requires Inspections)'}
              </span>
            }
          />
        </div>

        {!noIrrigation && (
          <div style={fieldWrap}>
            <span style={labelStyle}>Assigned Irrigation Tech</span>
            <Autocomplete
              options={crews || []}
              getOptionLabel={getCrewOptionLabel}
              getOptionSelected={getCrewOptionSelected}
              value={irrigationTech}
              onChange={(_, val) => setIrrigationTech(val)}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" size="small" placeholder="No tech assigned" />
              )}
            />
          </div>
        )}

        <div style={{ ...fieldWrap, marginBottom: 4 }}>
          <FormControlLabel
            control={<Switch checked={mowingRequired} onChange={(e) => setMowingRequired(e.target.checked)} color="primary" />}
            label={<span style={{ fontSize: 13, fontWeight: 600, color: V4_T.ink2 }}>Mowing Required</span>}
          />
        </div>
      </DialogContent>
      <DialogActions style={{ borderTop: `1px solid ${V4_T.line}`, padding: '12px 24px' }}>
        <Button onClick={onClose} style={{ color: V4_T.ink2 }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          style={{ fontWeight: 600, background: V4_T.green, color: '#fff' }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function PropertyDetailDrawer ({
  open,
  onClose,
  classes,
  selectedProperty,
  sidebarData,
  geoInsights,
  activeTab,
  onTabChange,
  hoursView,
  setHoursView,
  detailHistoryRange,
  setDetailHistoryRange,
  detailHistory,
  hoveredVisit,
  setHoveredVisit,
  pinnedVisit,
  setPinnedVisit,
  visitTypeFilter,
  setVisitTypeFilter,
  trendPct,
  marginSpark,
  visitsSpark,
  dayAbbrev,
  dominantService,
  propertyOpportunities,
  isProblematic,
  onEditProperty,
  renderTicketLink
}) {
  const kpis = sidebarData.kpis
  const driveVariance = kpis.driveActual - kpis.driveExpected
  const hasNormalizedMedian = kpis.normalizedVisitCount > 0

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <div className={classes.drawer}>
        <div className={classes.drawerHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10.5, color: V4_T.muteSoft, fontFamily: V4_T.mono, marginBottom: 6 }}>
            <span>PROPERTIES</span>
            <span>›</span>
            <span style={{ color: V4_T.ink2 }}>#{sidebarData.propertyId || '-'}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" className={classes.drawerTitle}>
                {selectedProperty?.name || 'Property Details'}
              </Typography>
              <Typography variant="body2" className={classes.drawerSubtitle}>
                {sidebarData.address || 'Address pending'}
              </Typography>
            </div>
            <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
              <Tooltip title="Edit property">
                <IconButton size="small" onClick={onEditProperty} style={{ border: `1px solid ${V4_T.line}`, borderRadius: 6, padding: 5 }}>
                  <EditIcon style={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Open in Aspire">
                <span>
                  <IconButton
                    size="small"
                    component="a"
                    href={sidebarData.aspireUrl || undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    disabled={!sidebarData.aspireUrl}
                    style={{ border: `1px solid ${V4_T.line}`, borderRadius: 6, padding: 5 }}
                  >
                    <OpenInNewIcon style={{ fontSize: 14 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="More actions">
                <IconButton size="small" style={{ border: `1px solid ${V4_T.line}`, borderRadius: 6, padding: 5 }}>
                  <MoreVertIcon style={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Close">
                <IconButton size="small" onClick={onClose} style={{ border: `1px solid ${V4_T.line}`, borderRadius: 6, padding: 5 }}>
                  <CloseIcon style={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </div>
          </div>
          <div className={classes.drawerChipRow}>
            {sidebarData.propertyId && <span className={`${classes.v4Chip} ${classes.v4MonoChip}`}>ASPIRE {sidebarData.propertyId}</span>}
            {sidebarData.companyChip && <span className={`${classes.v4Chip} ${classes.v4MonoChip}`}>{sidebarData.companyChip}</span>}
            {sidebarData.crewName && sidebarData.crewName !== '-' && (
              <span className={`${classes.v4Chip} ${classes.v4GreenChip}`}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: V4_T.green, marginRight: 5, flexShrink: 0 }} />
                {sidebarData.crewName}
              </span>
            )}
            {sidebarData.scheduledDay && <span className={classes.v4Chip}>{sidebarData.scheduledDay}</span>}
            {sidebarData.parkedCrewName && (
              <span className={`${classes.v4Chip} ${classes.v4AmberChip}`}>⚠ Parked · {sidebarData.parkedCrewName}</span>
            )}
            {sidebarData.scenarioUnscheduled && (
              <span className={`${classes.v4Chip} ${classes.v4AmberChip}`}>⚠ No day in scenario</span>
            )}
          </div>
          {(sidebarData.parkedCrewName || sidebarData.scenarioUnscheduled) && (
            <div className={classes.drawerScenarioNote}>
              {sidebarData.parkedCrewName
                ? `Parked in this scenario on "${sidebarData.parkedCrewName}"${sidebarData.parkedCrewInactive ? ' (inactive crew)' : ''}${sidebarData.parkedCrewDivision ? ` · ${sidebarData.parkedCrewDivision}` : ''} — not on any active maintenance board. Data below is the live Aspire schedule.`
                : 'In this scenario the property is on the route but not placed on a day. Data below is the live Aspire schedule.'}
            </div>
          )}
          <div className={classes.drawerQuickStats}>
            <DrawerQuickStat
              classes={classes}
              label="Est hours"
              value={`${formatNumber(kpis.hasInternalEst ? kpis.internalEstHours : kpis.estHoursPerVisit, 1)}h`}
              sub={kpis.hasInternalEst
                ? `internal · ${kpis.internalEstVisitCount} visit${kpis.internalEstVisitCount === 1 ? '' : 's'}`
                : 'contract setup'}
            />
            <DrawerQuickStat
              classes={classes}
              label="Median on-site"
              value={`${formatNumber(kpis.medianHours || kpis.avgHours, 2)}h`}
              sub={hasNormalizedMedian ? `${kpis.normalizedVisitCount} clock rows` : 'crew-hour fallback'}
              tone={!hasNormalizedMedian ? 'warn' : kpis.medianHours > kpis.estHoursPerVisit ? 'bad' : 'good'}
            />
            <DrawerQuickStat
              classes={classes}
              label="Drive time"
              value={`${formatNumber(kpis.driveActual, 2)}h`}
              sub={`vs ${formatNumber(kpis.driveExpected, 2)}h route`}
              tone={driveVariance > 0 ? 'bad' : 'good'}
            />
          </div>
        </div>

        <div className={classes.drawerTabs}>
          {PROPERTY_DETAIL_TABS.map(tab => (
            <button
              key={tab.value}
              type="button"
              onClick={() => onTabChange(tab.value)}
              className={`${classes.drawerTabButton} ${activeTab === tab.value ? classes.drawerTabButtonActive : ''}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className={classes.drawerKpiRibbon}>
          <div className={classes.drawerKpiGrid}>
            <RibbonKPI
              label="Margin"
              value={formatPercent(kpis.margin)}
              tone={kpis.margin < 10 ? 'bad' : 'good'}
              spark={marginSpark}
              color={kpis.margin < 10 ? V4_T.rust : V4_T.green}
            />
            <OvertimeKPI value={kpis.otHours} visits={sidebarData.visitRows} />
            <RibbonKPI label="Visits" value={kpis.completedVisits} unit={`/${kpis.annualVisits || 0}`} spark={visitsSpark} color={V4_T.orange} />
          </div>
        </div>

        <div className={classes.drawerContent}>
          {isProblematic && (
            <div className={classes.drawerAlert}>
              <WarningIcon fontSize="small" />
              Review pricing or route assumptions for this property.
            </div>
          )}

          {activeTab === 'overview' && (
            <>
              <DetailCard
                classes={classes}
                title="Location"
                subtitle={geoInsights.hasCoordinates
                  ? `${geoInsights.yardRoute ? formatMiles(geoInsights.yardRoute.distanceMiles) : formatMiles(geoInsights.yardRoadMiles)} from yard`
                  : 'coordinates pending'}
              >
                {geoInsights.hasCoordinates ? (
                  <>
                    <MiniRouteMap property={selectedProperty} geoInsights={geoInsights} classes={classes} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 2px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: V4_T.ink, border: `2px solid ${V4_T.surface}`, boxShadow: `0 0 0 1px ${V4_T.line}` }} />
                        <span style={{ fontSize: 8.5, fontWeight: 800, color: V4_T.muteSoft, marginTop: 3 }}>YARD</span>
                      </div>
                      <div style={{ flex: 1, position: 'relative', height: 22 }}>
                        <div style={{ position: 'absolute', top: 10, left: 0, right: 0, height: 2, background: V4_T.green, borderRadius: 1 }} />
                        <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <span style={{ fontSize: 12, fontWeight: 800, color: V4_T.ink, fontFamily: V4_T.mono, background: V4_T.surface, padding: '0 8px', lineHeight: 1.1 }}>
                            {geoInsights.yardRoute
                              ? `${formatMiles(geoInsights.yardRoute.distanceMiles)} · ${formatMinutes(geoInsights.yardRoute.durationMinutes)}`
                              : `${formatMiles(geoInsights.yardRoadMiles)} · ${formatMinutes(geoInsights.yardDriveMinutes)}`}
                          </span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: V4_T.green, border: `2px solid ${V4_T.surface}`, boxShadow: `0 0 0 1px ${V4_T.green}` }} />
                        <span style={{ fontSize: 8.5, fontWeight: 800, color: V4_T.green, marginTop: 3 }}>SITE</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', fontSize: 10.5, color: V4_T.muteSoft, marginTop: 4, padding: '0 4px' }}>
                      <span>{formatMiles(geoInsights.yardStraightMiles)} straight-line</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '9px 10px', marginTop: 10, borderRadius: 7, background: V4_T.surface2, border: `1px solid ${V4_T.lineSoft}` }}>
                      <span style={{ width: 22, height: 22, borderRadius: '50%', background: V4_T.greenSoft, color: V4_T.green, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>●</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 9.5, fontWeight: 800, color: V4_T.muteSoft, textTransform: 'uppercase' }}>Nearest customer</div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: V4_T.ink, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {geoInsights.nearestSite?.name || 'None found'}
                        </div>
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 700, color: V4_T.green, fontFamily: V4_T.mono, flexShrink: 0 }}>
                        {geoInsights.nearestSite ? formatMiles(geoInsights.nearestSite.distanceMiles) : '-'}
                      </span>
                    </div>
                    <div style={{ marginTop: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 6 }}>
                        <span style={{ fontSize: 9.5, fontWeight: 800, color: V4_T.muteSoft, textTransform: 'uppercase' }}>Neighborhood density</span>
                        <span style={{ marginLeft: 'auto', fontSize: 10.5, color: V4_T.mute, fontFamily: V4_T.mono }}>
                          avg <b style={{ color: V4_T.ink2 }}>{formatMiles(geoInsights.avgDistanceWithinFiveMiles)}</b> · med <b style={{ color: V4_T.ink2 }}>{formatMiles(geoInsights.medianDistanceWithinFiveMiles)}</b>
                        </span>
                      </div>
                      {[
                        ['1 MI', geoInsights.sitesWithinOneMile.length],
                        ['3 MI', geoInsights.sitesWithinThreeMiles.length],
                        ['5 MI', geoInsights.sitesWithinFiveMiles.length]
                      ].map(([label, count]) => {
                        const maxCount = Math.max(1, geoInsights.sitesWithinFiveMiles.length)
                        return (
                          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 5 }}>
                            <span style={{ fontSize: 10, fontWeight: 700, color: V4_T.muteSoft, fontFamily: V4_T.mono, width: 32, flexShrink: 0 }}>{label}</span>
                            <div style={{ flex: 1, height: 14, background: V4_T.cream, borderRadius: 3, position: 'relative', overflow: 'hidden' }}>
                              <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${clamp((count / maxCount) * 100, 0, 100)}%`, background: V4_T.green, opacity: 0.75, borderRadius: 3 }} />
                              <span style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 10, fontWeight: 700, color: V4_T.ink2, fontFamily: V4_T.mono }}>sites</span>
                            </div>
                            <span style={{ fontSize: 12, fontWeight: 800, color: V4_T.ink, fontFamily: V4_T.mono, minWidth: 30, textAlign: 'right' }}>{count}</span>
                          </div>
                        )
                      })}
                    </div>
                  </>
                ) : (
                  <div className={classes.emptyDrawerNote}>No coordinates available for nearby-site or yard travel estimates.</div>
                )}
              </DetailCard>

              <DetailCard classes={classes} title="Cadence" action="Schedule" onAction={() => onTabChange('schedule')} tight>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, background: V4_T.greenSoft, border: '1px solid #c7dac8', marginTop: 2 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '3px 9px', borderRadius: 6, background: V4_T.green, color: V4_T.surface, flexShrink: 0 }}>
                    <span style={{ fontSize: 9, fontWeight: 700 }}>EVERY</span>
                    <span style={{ fontSize: 13, fontWeight: 800 }}>{dayAbbrev}</span>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: V4_T.ink }}>{sidebarData.crewName || 'Crew pending'}</div>
                    <div style={{ fontSize: 11, color: V4_T.mute, marginTop: 1 }}>
                      Maintenance · {kpis.completedVisits} done · <span style={{ color: V4_T.ink2, fontWeight: 600 }}>{kpis.futureVisits} ahead</span>
                    </div>
                  </div>
                </div>
              </DetailCard>

              <HoursPerVisitCard
                classes={classes}
                sidebarData={sidebarData}
                hoursView={hoursView}
                setHoursView={setHoursView}
                detailHistoryRange={detailHistoryRange}
                setDetailHistoryRange={setDetailHistoryRange}
                hoveredVisit={hoveredVisit}
                setHoveredVisit={setHoveredVisit}
                pinnedVisit={pinnedVisit}
                setPinnedVisit={setPinnedVisit}
                visitTypeFilter={visitTypeFilter}
                setVisitTypeFilter={setVisitTypeFilter}
                trendPct={trendPct}
              />

              <CrewTimeCard classes={classes} sidebarData={sidebarData} />
            </>
          )}

          {activeTab === 'history' && (
            <HistoryTab
              classes={classes}
              sidebarData={sidebarData}
              detailHistoryRange={detailHistoryRange}
              setDetailHistoryRange={setDetailHistoryRange}
              detailHistory={detailHistory}
              hoveredVisit={hoveredVisit}
              setHoveredVisit={setHoveredVisit}
              pinnedVisit={pinnedVisit}
              setPinnedVisit={setPinnedVisit}
              visitTypeFilter={visitTypeFilter}
              setVisitTypeFilter={setVisitTypeFilter}
            />
          )}

          {activeTab === 'service' && (
            <ServiceTab classes={classes} sidebarData={sidebarData} dominantService={dominantService} />
          )}

          {activeTab === 'schedule' && (
            <ScheduleTab classes={classes} selectedProperty={selectedProperty} sidebarData={sidebarData} dayAbbrev={dayAbbrev} renderTicketLink={renderTicketLink} />
          )}

          {activeTab === 'financials' && (
            <FinancialsTab classes={classes} sidebarData={sidebarData} />
          )}
        </div>
      </div>
    </Drawer>
  )
}

function HoursPerVisitCard ({
  classes,
  sidebarData,
  hoursView,
  setHoursView,
  detailHistoryRange,
  setDetailHistoryRange,
  hoveredVisit,
  setHoveredVisit,
  pinnedVisit,
  setPinnedVisit,
  visitTypeFilter,
  setVisitTypeFilter,
  trendPct
}) {
  const kpis = sidebarData.kpis
  const visibleVisitRows = filterVisitRowsByType(sidebarData.visitRows, visitTypeFilter)
  const visibleTrend = getVisitTrendStats(visibleVisitRows, kpis.avgHours)
  const activeVisit = pinnedVisit || hoveredVisit
  const previewVisit = (visit) => {
    if (!pinnedVisit) setHoveredVisit(visit)
  }
  const openVisit = (visit) => {
    const visitUrl = getAspireWorkTicketVisitUrl(visit) || getAspireWorkTicketUrl(visit)
    if (visitUrl) window.open(visitUrl, '_blank', 'noopener,noreferrer')
  }

  return (
    <DetailCard
      classes={classes}
      title="Hours per visit"
      subtitle="trailing year"
      right={
        <SegmentedControl
          value={hoursView}
          options={[{ value: 'visits', label: 'Visits' }, { value: 'months', label: 'Months' }]}
          onChange={setHoursView}
        />
      }
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 26, fontWeight: 800, color: kpis.estVariance >= 0 ? V4_T.green : V4_T.rust, lineHeight: 1 }}>{formatNumber(kpis.avgHours, 1)}h</span>
        <span style={{ fontSize: 11.5, color: V4_T.mute, whiteSpace: 'nowrap' }}>
          avg · vs <span style={{ fontFamily: V4_T.mono, color: V4_T.ink2, fontWeight: 600 }}>{formatNumber(kpis.estHoursPerVisit, 1)}h</span> est
        </span>
        <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: kpis.estVariance >= 0 ? '#164a2c' : V4_T.rust, padding: '3px 9px', borderRadius: 99, background: kpis.estVariance >= 0 ? V4_T.greenSoft : V4_T.rustSoft, whiteSpace: 'nowrap' }}>
          {Math.abs(kpis.estVariance).toFixed(1)}h {kpis.estVariance >= 0 ? 'under' : 'over'}
        </span>
      </div>

      {hoursView === 'months' ? (
        <>
          <div style={{ background: V4_T.cream, borderRadius: 8, padding: '6px 4px 2px', margin: '0 -3px' }}>
            <MonthlyHoursBars monthly={sidebarData.monthlyRows} est={kpis.estHoursPerVisit} />
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 7, fontSize: 10.5, color: V4_T.mute, alignItems: 'center' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 9, height: 9, borderRadius: 2, background: V4_T.green }} /> Actual</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 14, borderTop: `1.5px dashed ${V4_T.rust}` }} /> Estimate</span>
            <span style={{ marginLeft: 'auto', color: trendPct > 0 ? V4_T.rust : V4_T.green, fontWeight: 700 }}>{trendPct > 0 ? 'Trending up' : 'Trending down'}</span>
          </div>
        </>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 11.5, color: V4_T.mute }}>
              <b style={{ color: V4_T.ink, fontFamily: V4_T.mono }}>{formatNumber(visibleTrend.recentAvg, 2)}h</b> recent avg
              <span style={{ color: visibleTrend.trendPct > 0 ? V4_T.rust : V4_T.green, fontWeight: 700, marginLeft: 6 }}>{visibleTrend.trendPct > 0 ? '+' : ''}{visibleTrend.trendPct}%</span>
              <span style={{ color: V4_T.muteSoft }}> vs prior 8wk</span>
            </span>
            <div style={{ marginLeft: 'auto' }}>
              <SegmentedControl
                value={detailHistoryRange}
                options={[{ value: '30', label: '30d' }, { value: '90', label: '90d' }, { value: 'year', label: '1y' }]}
                onChange={setDetailHistoryRange}
              />
            </div>
          </div>
          <div style={{ background: V4_T.cream, borderRadius: 8, padding: '6px 2px 2px', margin: '0 -3px' }}>
            <HoursDotPlot
              visits={visibleVisitRows}
              est={kpis.estHoursPerVisit}
              onHover={previewVisit}
              onSelect={setPinnedVisit}
              onOpen={openVisit}
            />
          </div>
          <VisitLegend value={visitTypeFilter} onChange={(nextFilter) => {
            setVisitTypeFilter(nextFilter)
            setHoveredVisit(null)
            setPinnedVisit(null)
          }} />
          {activeVisit && <HoverStrip visit={activeVisit} pinned={!!pinnedVisit} onClear={() => {
            setPinnedVisit(null)
            setHoveredVisit(null)
          }} />}
        </>
      )}

      <div style={{ marginTop: 10, padding: '10px 12px', background: kpis.estVariance >= 0 ? V4_T.greenSoft : V4_T.rustSoft, border: `1px solid ${kpis.estVariance >= 0 ? '#c7dac8' : '#e8c4a8'}`, borderRadius: 8, fontSize: 11.5, color: kpis.estVariance >= 0 ? '#164a2c' : '#7a3115', lineHeight: 1.45 }}>
        Crew runs <strong>{Math.abs(kpis.estVariance).toFixed(1)}h {kpis.estVariance >= 0 ? 'under' : 'over'} estimate per visit</strong> based on current routing rollups.
      </div>
    </DetailCard>
  )
}

function HoverStrip ({ visit, pinned = false, onClear }) {
  const visitUrl = getAspireWorkTicketVisitUrl(visit)
  const ticketUrl = getAspireWorkTicketUrl(visit)
  const linkUrl = visitUrl || ticketUrl
  const linkLabel = visitUrl ? 'Open visit' : getTicketLabel(visit)

  return (
    <div style={{ marginTop: 8, padding: '6px 9px', background: V4_T.greenSofter, border: '1px solid #cfe0d0', borderRadius: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, fontSize: 11.5 }}>
      <span style={{ fontFamily: V4_T.mono, color: V4_T.ink2 }}>{visit.date}</span>
      <span style={{ color: V4_T.mute, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{visit.crew}</span>
      <span style={{ fontWeight: 700, color: V4_T.ink, fontFamily: V4_T.mono }}>{formatNumber(visit.hrs, 2)}h</span>
      {linkUrl && (
        <MuiLink
          href={linkUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(event) => event.stopPropagation()}
          style={{ color: V4_T.green, fontWeight: 800, fontFamily: V4_T.mono, whiteSpace: 'nowrap' }}
        >
          {linkLabel}
        </MuiLink>
      )}
      {pinned && (
        <button
          type="button"
          onClick={onClear}
          style={{ border: 'none', background: 'transparent', color: V4_T.muteSoft, cursor: 'pointer', fontSize: 13, fontWeight: 800, lineHeight: 1, padding: 2 }}
          aria-label="Clear selected visit"
          title="Clear selected visit"
        >
          x
        </button>
      )}
    </div>
  )
}

function VisitLegend ({ value = 'all', onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 7, flexWrap: 'wrap', fontSize: 10.5 }}>
      <button
        type="button"
        onClick={() => onChange && onChange('all')}
        style={{
          border: `1px solid ${value === 'all' ? V4_T.green : V4_T.line}`,
          background: value === 'all' ? V4_T.greenSofter : 'transparent',
          borderRadius: 999,
          padding: '2px 8px',
          cursor: 'pointer',
          fontFamily: 'inherit',
          fontSize: 10.5,
          fontWeight: 800,
          color: value === 'all' ? V4_T.ink : V4_T.mute
        }}
        aria-pressed={value === 'all'}
        title="Show all visit dots"
      >
        All
      </button>
      {[['Maint', 'Maintenance'], ['Irrig', 'Irrigation'], ['Herb', 'Herbicide']].map(([key, label]) => {
        const active = value === key
        return (
          <button
            key={key}
            type="button"
            onClick={() => onChange && onChange(active ? 'all' : key)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              border: `1px solid ${active ? V4_T.green : 'transparent'}`,
              background: active ? V4_T.greenSofter : 'transparent',
              borderRadius: 999,
              padding: '2px 7px',
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: 10.5
            }}
            aria-pressed={active}
            title={`Show ${label} dots`}
          >
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: SERVICE_COLORS[key] }} />
            <span style={{ fontWeight: 700, color: active ? V4_T.ink : V4_T.mute }}>{label}</span>
          </button>
        )
      })}
      <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
        <span style={{ width: 14, borderTop: `1.5px dashed ${V4_T.rust}` }} />
        <span style={{ fontWeight: 600, color: V4_T.rust }}>contract target</span>
      </span>
    </div>
  )
}

function CrewTimeCard ({ classes, sidebarData }) {
  const total = sidebarData.kpis.hoursActual
  const onSite = sidebarData.kpis.avgHours
  const driveActual = sidebarData.kpis.driveActual
  const driveExpected = sidebarData.kpis.driveExpected
  const driveVariance = driveActual - driveExpected
  const scaleMax = Math.max(total, onSite + driveExpected, 1) * 1.05
  const onSitePct = (onSite / scaleMax) * 100
  const drivePct = (driveActual / scaleMax) * 100
  const expectedEndPct = ((onSite + driveExpected) / scaleMax) * 100

  return (
    <DetailCard classes={classes} title="Crew time per visit" subtitle="actual · incl. drive">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 24, fontWeight: 800, color: V4_T.ink, lineHeight: 1 }}>{formatNumber(total, 2)}h</span>
        <span style={{ fontSize: 11.5, color: V4_T.mute, whiteSpace: 'nowrap' }}>
          avg total · <span style={{ color: V4_T.green, fontWeight: 700, fontFamily: V4_T.mono }}>{formatNumber(onSite, 2)}</span> on-site + <span style={{ color: V4_T.amber, fontWeight: 700, fontFamily: V4_T.mono }}>{formatNumber(driveActual, 2)}</span> drive
        </span>
      </div>
      <div style={{ position: 'relative', height: 28, background: V4_T.cream, borderRadius: 6, overflow: 'hidden', border: `1px solid ${V4_T.lineSoft}` }}>
        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${clamp(onSitePct, 0, 100)}%`, background: V4_T.green }} />
        <div style={{ position: 'absolute', left: `${clamp(onSitePct, 0, 100)}%`, top: 0, bottom: 0, width: `${clamp(drivePct, 0, 100)}%`, background: driveVariance > 0 ? V4_T.rust : V4_T.amber, opacity: 0.92 }} />
        <div style={{ position: 'absolute', left: `calc(${clamp(expectedEndPct, 0, 100)}% - 1px)`, top: -3, bottom: -3, width: 2, background: V4_T.ink, borderRadius: 1 }} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
        <MiniBreakdown label="On-site" value={`${formatNumber(onSite, 2)}h`} sub="productive work" color={V4_T.green} />
        <MiniBreakdown label="Drive" value={`${formatNumber(driveActual, 2)}h`} sub={`vs ${formatNumber(driveExpected, 2)}h expected`} color={driveVariance > 0 ? V4_T.rust : V4_T.amber} delta={`${driveVariance > 0 ? '+' : ''}${formatNumber(driveVariance, 2)}`} />
      </div>
      {driveVariance > 0 && (
        <div style={{ marginTop: 10, padding: '9px 11px', background: V4_T.rustSoft, border: '1px solid #e8c4a8', borderRadius: 7, fontSize: 11.5, color: '#7a3115', lineHeight: 1.45 }}>
          Driving runs <strong>{formatNumber(driveVariance, 2)}h over expected per visit</strong> - about <strong>{formatNumber(driveVariance * sidebarData.kpis.completedVisits, 1)} extra hours</strong> across {sidebarData.kpis.completedVisits} completed visits.
        </div>
      )}
    </DetailCard>
  )
}

function MiniBreakdown ({ label, value, sub, color, delta }) {
  return (
    <div style={{ padding: '8px 10px', borderRadius: 7, background: V4_T.surface2, border: `1px solid ${V4_T.lineSoft}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 3 }}>
        <span style={{ width: 7, height: 7, borderRadius: 2, background: color }} />
        <span style={{ fontSize: 9, fontWeight: 800, color: V4_T.muteSoft, textTransform: 'uppercase' }}>{label}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
        <span style={{ fontSize: 14, fontWeight: 800, color: V4_T.ink, fontFamily: V4_T.mono, lineHeight: 1.1 }}>{value}</span>
        {delta && <span style={{ fontSize: 10, fontWeight: 700, color, fontFamily: V4_T.mono }}>{delta}</span>}
      </div>
      <div style={{ fontSize: 10.5, color: V4_T.muteSoft, marginTop: 2 }}>{sub}</div>
    </div>
  )
}

function HistoryTab ({ classes, sidebarData, detailHistoryRange, setDetailHistoryRange, detailHistory, hoveredVisit, setHoveredVisit, pinnedVisit, setPinnedVisit, visitTypeFilter, setVisitTypeFilter }) {
  const visibleVisitRows = filterVisitRowsByType(sidebarData.visitRows, visitTypeFilter)
  const visibleTrend = getVisitTrendStats(visibleVisitRows, sidebarData.kpis.avgHours)
  const activeVisit = pinnedVisit || hoveredVisit
  const VISIT_LOG_PAGE = 12
  const [visitLogVisible, setVisitLogVisible] = useState(VISIT_LOG_PAGE)
  // Reset pagination when the underlying data set or active filter changes.
  useEffect(() => { setVisitLogVisible(VISIT_LOG_PAGE) }, [sidebarData.visitRows, visitTypeFilter])
  const previewVisit = (visit) => {
    if (!pinnedVisit) setHoveredVisit(visit)
  }
  const openVisit = (visit) => {
    const visitUrl = getAspireWorkTicketVisitUrl(visit) || getAspireWorkTicketUrl(visit)
    if (visitUrl) window.open(visitUrl, '_blank', 'noopener,noreferrer')
  }

  return (
    <>
      <DetailCard
        classes={classes}
        title="Visit timeline"
        subtitle={`${formatNumber(visibleTrend.recentAvg, 1)}h recent avg`}
        right={
          <SegmentedControl
            value={detailHistoryRange}
            options={[{ value: '30', label: '30d' }, { value: '90', label: '90d' }, { value: 'year', label: '1y' }]}
            onChange={setDetailHistoryRange}
          />
        }
      >
        {detailHistory.error && <div className={classes.emptyDrawerNote} style={{ marginBottom: 8 }}>Could not load synced work-ticket visit history: {detailHistory.error}</div>}
        <div style={{ background: V4_T.cream, borderRadius: 8, padding: '6px 2px 2px', margin: '0 -3px' }}>
          <HoursDotPlot
            visits={visibleVisitRows}
            est={sidebarData.kpis.estHoursPerVisit}
            onHover={previewVisit}
            onSelect={setPinnedVisit}
            onOpen={openVisit}
            height={134}
          />
        </div>
        <VisitLegend value={visitTypeFilter} onChange={(nextFilter) => {
          setVisitTypeFilter(nextFilter)
          setHoveredVisit(null)
          setPinnedVisit(null)
        }} />
        <div style={{ marginTop: 6, fontSize: 10.5, fontWeight: 700, color: visibleTrend.trendPct > 0 ? V4_T.rust : V4_T.green, textAlign: 'right' }}>
          {visibleTrend.trendPct > 0 ? '+' : ''}{visibleTrend.trendPct}% vs prior 8wk
        </div>
        {activeVisit && <HoverStrip visit={activeVisit} pinned={!!pinnedVisit} onClear={() => {
          setPinnedVisit(null)
          setHoveredVisit(null)
        }} />}
      </DetailCard>

      <DetailCard
        classes={classes}
        title="Visit log"
        subtitle={visitTypeFilter === 'all'
          ? `${sidebarData.kpis.completedVisits} completed · 12mo`
          : `${visibleVisitRows.length} of ${sidebarData.visitRows.length} · ${visitTypeFilter === 'Maint' ? 'maintenance' : visitTypeFilter === 'Irrig' ? 'irrigation' : visitTypeFilter === 'Herb' ? 'herbicide' : visitTypeFilter}`}
        action={detailHistory.loading ? 'Loading' : 'Export'}
      >
        {visibleVisitRows.slice(0, visitLogVisible).map((visit, index) => {
          const color = SERVICE_COLORS[visit.type] || V4_T.mute
          const serviceLabel = { Maint: 'Weekly maintenance', Irrig: 'Irrigation service', Herb: 'Herbicide application', Other: 'Service visit' }[visit.type]
          const ticketLabel = getTicketLabel(visit)
          const ticketUrl = getAspireWorkTicketUrl(visit) || getAspireWorkTicketVisitUrl(visit)
          const { chips, fallback: peopleFallback, overflow } = getVisitPeopleChips(visit)
          const timeRange = getVisitTimeRange(visit)
          const timeEntryUrl = getAspireTimeEntryUrl(visit)
          const driveHours = getVisitAssociatedDriveHours(visit)
          return (
            <div key={visit.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0', borderBottom: index < Math.min(visibleVisitRows.length, visitLogVisible) - 1 ? `1px solid ${V4_T.lineSoft}` : 'none' }}>
              <span style={{ width: 3, alignSelf: 'stretch', background: color, borderRadius: 2, opacity: visit.flag === 'high' ? 1 : 0.7, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: V4_T.ink, fontFamily: V4_T.mono }}>{visit.date}</span>
                  {visit.flag === 'high' && <span style={{ fontSize: 9, fontWeight: 800, color: V4_T.rust, padding: '1px 5px', background: V4_T.rustSoft, borderRadius: 3 }}>OUTLIER</span>}
                </div>
                <div style={{ fontSize: 10.5, color: V4_T.mute, marginTop: 1, lineHeight: 1.4 }}>{serviceLabel}</div>
                {chips.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 4 }}>
                    {chips.map(chip => (
                      <div key={chip.name} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 18,
                            height: 18,
                            borderRadius: '50%',
                            background: chip.color,
                            color: '#fff',
                            fontSize: 8.5,
                            fontWeight: 800,
                            fontFamily: V4_T.mono,
                            letterSpacing: '-0.02em',
                            flexShrink: 0
                          }}
                        >
                          {chip.initials}
                        </span>
                        <span style={{ fontSize: 10.5, color: V4_T.ink2, lineHeight: 1.2 }}>{chip.name}</span>
                      </div>
                    ))}
                    {overflow > 0 && (
                      <div style={{ fontSize: 10, color: V4_T.muteSoft, fontFamily: V4_T.mono, fontWeight: 700, marginLeft: 24 }}>+{overflow} more</div>
                    )}
                  </div>
                ) : peopleFallback ? (
                  <div style={{ fontSize: 10.5, color: V4_T.muteSoft, marginTop: 2 }}>{peopleFallback}</div>
                ) : null}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0, gap: 2 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: V4_T.ink, fontFamily: V4_T.mono }}>{formatNumber(visit.hrs, 2)}h</div>
                <div style={{ fontSize: 9.5, color: V4_T.green, fontFamily: V4_T.mono, fontWeight: 600 }}>
                  {ticketUrl && ticketLabel ? (
                    <MuiLink
                      href={ticketUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(event) => event.stopPropagation()}
                      style={{ color: V4_T.green, fontWeight: 800, fontFamily: V4_T.mono }}
                    >
                      {ticketLabel}
                    </MuiLink>
                  ) : (
                    ticketLabel || 'TX -'
                  )}
                </div>
                {timeRange && (
                  timeEntryUrl ? (
                    <MuiLink
                      href={timeEntryUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(event) => event.stopPropagation()}
                      style={{ fontSize: 11, color: V4_T.ink2, fontFamily: V4_T.mono, fontWeight: 600, marginTop: 2, whiteSpace: 'nowrap', textDecoration: 'underline', textDecorationColor: V4_T.lineSoft, textUnderlineOffset: 2 }}
                    >
                      {timeRange}
                    </MuiLink>
                  ) : (
                    <span style={{ fontSize: 11, color: V4_T.muteSoft, fontFamily: V4_T.mono, marginTop: 2, whiteSpace: 'nowrap' }}>{timeRange}</span>
                  )
                )}
                {driveHours > 0 && (
                  <span
                    title="HoursAct − OnSiteHours"
                    style={{ fontSize: 10, color: V4_T.muteSoft, fontFamily: V4_T.mono, marginTop: 1, whiteSpace: 'nowrap' }}
                  >
                    +{formatNumber(driveHours, 2)}h drive
                  </span>
                )}
              </div>
            </div>
          )
        })}
        {visibleVisitRows.length > VISIT_LOG_PAGE && (
          <div style={{ fontSize: 10, color: V4_T.muteSoft, marginTop: 10, textAlign: 'center', fontFamily: V4_T.mono }}>
            SHOWING {Math.min(visitLogVisible, visibleVisitRows.length)} OF {visibleVisitRows.length}
            {visitLogVisible < visibleVisitRows.length ? (
              <>
                {' · '}
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => setVisitLogVisible(v => Math.min(v + VISIT_LOG_PAGE, visibleVisitRows.length))}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setVisitLogVisible(v => Math.min(v + VISIT_LOG_PAGE, visibleVisitRows.length)) }}
                  style={{ color: V4_T.green, fontWeight: 700, cursor: 'pointer' }}
                >
                  LOAD MORE →
                </span>
              </>
            ) : (
              <>
                {' · '}
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => setVisitLogVisible(VISIT_LOG_PAGE)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setVisitLogVisible(VISIT_LOG_PAGE) }}
                  style={{ color: V4_T.green, fontWeight: 700, cursor: 'pointer' }}
                >
                  SHOW LESS ↑
                </span>
              </>
            )}
          </div>
        )}
        {visibleVisitRows.length === 0 && (
          <div style={{ fontSize: 11, color: V4_T.muteSoft, marginTop: 8, textAlign: 'center', fontStyle: 'italic' }}>
            No visits match the current filter.
          </div>
        )}
      </DetailCard>
    </>
  )
}

function ServiceTab ({ classes, sidebarData, dominantService }) {
  return (
    <>
      <DetailCard classes={classes} title="Service mix" subtitle="this year · by hours">
        <ServiceBars services={sidebarData.serviceRows} />
        <div style={{ marginTop: 12, padding: '9px 11px', background: V4_T.surface2, borderRadius: 7, border: `1px solid ${V4_T.lineSoft}`, fontSize: 11.5, color: V4_T.ink2, lineHeight: 1.45 }}>
          {dominantService ? <><strong style={{ color: V4_T.ink }}>{dominantService.name}</strong> is the largest service share at {Math.round(dominantService.share * 100)}% of tracked hours.</> : 'Service-level hours are not available for this property yet.'}
        </div>
      </DetailCard>
      {sidebarData.serviceRows.map(service => {
        const color = SERVICE_COLORS[service.name] || V4_T.mute
        const completion = service.tickets ? service.completed / service.tickets : 0
        return (
          <DetailCard key={service.name} classes={classes} title={service.name} subtitle="details">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 4 }}>
              {[
                ['Tickets', service.tickets],
                ['Done', service.completed],
                ['Future', service.future],
                ['Hours', formatNumber(service.hours, 1)],
                ['$ Cost', formatCurrencyOrPending(service.cost)]
              ].map(([label, value]) => (
                <div key={label} style={{ textAlign: 'center', padding: '6px 0', minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: V4_T.ink, fontFamily: V4_T.mono, lineHeight: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</div>
                  <div style={{ fontSize: 9, color: V4_T.muteSoft, fontWeight: 700, marginTop: 5, textTransform: 'uppercase' }}>{label}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 10 }}>
              <div style={{ height: 5, background: V4_T.cream, borderRadius: 3, overflow: 'hidden', border: `1px solid ${V4_T.lineSoft}` }}>
                <div style={{ width: `${clamp(completion * 100, 0, 100)}%`, height: '100%', background: color }} />
              </div>
              <div style={{ fontSize: 10.5, color: V4_T.mute, marginTop: 4 }}>{Math.round(completion * 100)}% complete · {service.future} remaining</div>
            </div>
          </DetailCard>
        )
      })}
    </>
  )
}

function ScheduleTab ({ classes, selectedProperty, sidebarData, dayAbbrev, renderTicketLink }) {
  return (
    <>
      <DetailCard classes={classes} title="Cadence" subtitle={`${sidebarData.scheduledDay || 'unscheduled'} recurrence`}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 9, background: V4_T.greenSoft, border: '1px solid #c7dac8', marginBottom: 11 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '5px 11px', borderRadius: 7, background: V4_T.green, color: V4_T.surface, flexShrink: 0 }}>
            <span style={{ fontSize: 9.5, fontWeight: 700 }}>EVERY</span>
            <span style={{ fontSize: 15, fontWeight: 800 }}>{dayAbbrev}</span>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: V4_T.ink }}>{sidebarData.crewName || 'Crew pending'}</div>
            <div style={{ fontSize: 11.5, color: V4_T.mute, marginTop: 2 }}>Maintenance · active recurrence</div>
            <div style={{ fontSize: 10.5, color: V4_T.muteSoft, marginTop: 4, fontFamily: V4_T.mono }}>{sidebarData.kpis.completedVisits} DONE · {sidebarData.kpis.futureVisits} AHEAD</div>
          </div>
        </div>
        <div style={{ fontSize: 9.5, fontWeight: 800, color: V4_T.muteSoft, textTransform: 'uppercase', marginBottom: 6 }}>Recurrence</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
          {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day, index) => {
            const active = String(sidebarData.scheduledDay || '').toLowerCase() === day.toLowerCase()
            return (
              <div key={day} style={{ aspectRatio: '1/1', borderRadius: 6, background: active ? V4_T.green : V4_T.cream, border: `1px solid ${active ? V4_T.green : V4_T.line}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: active ? V4_T.surface : V4_T.muteSoft, fontFamily: V4_T.mono }}>
                {['M', 'T', 'W', 'T', 'F', 'S', 'S'][index]}
              </div>
            )
          })}
        </div>
      </DetailCard>

      <DetailCard classes={classes} title="Upcoming visits" subtitle={`${sidebarData.kpis.futureVisits} scheduled`} action="Export">
        {(selectedProperty?.futureVisits || []).slice(0, 8).map((visit, index) => {
          const date = visit.date ? new Date(visit.date) : null
          const validDate = date && !isNaN(date.getTime())
          return (
            <div key={`${visit.workTicketId || visit.workTicketNumber || index}`} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '9px 0', borderBottom: index < Math.min(selectedProperty.futureVisits.length, 8) - 1 ? `1px solid ${V4_T.lineSoft}` : 'none' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 36, padding: '4px 0', background: V4_T.cream, borderRadius: 5, border: `1px solid ${V4_T.line}` }}>
                <span style={{ fontSize: 9, fontWeight: 700, color: V4_T.muteSoft, textTransform: 'uppercase' }}>{getDayAbbrev(visit.dayOfWeek || (validDate ? date.toLocaleString([], { weekday: 'short' }) : ''))}</span>
                <span style={{ fontSize: 13, fontWeight: 800, color: V4_T.ink, lineHeight: 1.05, fontFamily: V4_T.mono }}>{validDate ? date.getDate() : '-'}</span>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: V4_T.ink }}>{validDate ? date.toLocaleDateString() : 'Date pending'}</div>
                <div style={{ fontSize: 10.5, color: V4_T.mute, marginTop: 1 }}>{visit.routeName || visit.crewLeaderName || sidebarData.crewName || 'Crew pending'}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: V4_T.ink, fontFamily: V4_T.mono }}>{formatNumber(visit.estimatedHours, 2)}h</div>
                <div style={{ fontSize: 9.5, color: V4_T.green, fontFamily: V4_T.mono, fontWeight: 600, marginTop: 1 }}>{renderTicketLink(visit)} </div>
              </div>
            </div>
          )
        })}
        {(!selectedProperty?.futureVisits || selectedProperty.futureVisits.length === 0) && <div className={classes.emptyDrawerNote}>No upcoming visits are attached to this property yet.</div>}
      </DetailCard>
    </>
  )
}

function FinancialsTab ({ classes, sidebarData }) {
  const kpis = sidebarData.kpis
  return (
    <>
      <DetailCard classes={classes} title="Money in / out" subtitle="YTD by month" action="Details">
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 22, fontWeight: 800, color: V4_T.ink, lineHeight: 1 }}>{formatCompactCurrency(kpis.totalRev)}</span>
          <span style={{ fontSize: 10.5, color: V4_T.mute, whiteSpace: 'nowrap' }}>earned revenue · YTD</span>
          <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: kpis.totalRev - kpis.actualLabor >= 0 ? V4_T.green : V4_T.rust, fontFamily: V4_T.mono, whiteSpace: 'nowrap' }}>Δ {formatCurrency(kpis.totalRev - kpis.actualLabor)}</span>
        </div>
        <div style={{ background: V4_T.cream, borderRadius: 8, padding: '8px 4px 2px', margin: '0 -3px' }}>
          <RevCostBars monthly={sidebarData.monthlyRows} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 6, marginTop: 9 }}>
          <MoneyLegend color={V4_T.green} label="Maintenance" value={formatCurrency(kpis.earnedRev)} />
          <MoneyLegend color={V4_T.amber} label="Other rev" value={formatCurrency(kpis.othRev)} sub="non-maint earned" />
          <MoneyLegend color={V4_T.mute} label="Labor cost" value={formatCurrency(kpis.actualLabor)} />
        </div>
      </DetailCard>

      <DetailCard classes={classes} title="Financials" subtitle="year to date">
        {[
          ['Maintenance revenue', formatCurrency(kpis.earnedRev), 'good', null],
          ['Other revenue', formatCurrency(kpis.othRev), 'other', 'non-maint earned'],
          ['Total earned revenue', formatCurrency(kpis.totalRev), 'bold', null],
          ['Estimated labor', formatCurrency(kpis.estLabor), null, 'should-be'],
          ['Actual labor', formatCurrency(kpis.actualLabor), kpis.costVariance >= 0 ? 'good' : 'bad', `${kpis.costVariance >= 0 ? '-' : '+'}${formatCurrency(Math.abs(kpis.costVariance))} vs est`],
          ['Maintenance margin', formatPercent(kpis.margin), kpis.margin < 10 ? 'bad' : 'good', `${formatCurrency(kpis.netProfit)} net`]
        ].map((row, index) => (
          <div key={row[0]} style={{ display: 'flex', alignItems: 'center', padding: '9px 2px', borderTop: index > 0 ? `1px solid ${V4_T.lineSoft}` : 'none', background: row[2] === 'bold' ? V4_T.surface2 : 'transparent', marginLeft: row[2] === 'bold' ? -8 : 0, marginRight: row[2] === 'bold' ? -8 : 0, paddingLeft: row[2] === 'bold' ? 10 : 2, paddingRight: row[2] === 'bold' ? 10 : 2 }}>
            <span style={{ flex: 1, fontSize: 12, color: row[2] === 'bold' ? V4_T.ink : V4_T.ink2, fontWeight: row[2] === 'bold' ? 700 : 400 }}>{row[0]}</span>
            {row[3] && <span style={{ fontSize: 10.5, color: V4_T.muteSoft, marginRight: 10, fontFamily: V4_T.mono }}>{row[3]}</span>}
            <span style={{ fontSize: 13.5, fontWeight: 800, color: row[2] === 'good' ? V4_T.green : row[2] === 'bad' ? V4_T.rust : row[2] === 'other' ? '#a87320' : V4_T.ink, fontFamily: V4_T.mono }}>{row[1]}</span>
          </div>
        ))}
      </DetailCard>
    </>
  )
}

function MoneyLegend ({ color, label, value, sub }) {
  return (
    <div style={{ padding: '7px 9px', borderRadius: 7, background: V4_T.surface2, border: `1px solid ${V4_T.lineSoft}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 3 }}>
        <span style={{ width: 7, height: 7, borderRadius: 2, background: color, flexShrink: 0 }} />
        <span style={{ fontSize: 9, fontWeight: 800, color: V4_T.muteSoft, textTransform: 'uppercase', letterSpacing: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
      </div>
      <div style={{ fontSize: 12.5, fontWeight: 800, color: V4_T.ink, fontFamily: V4_T.mono, lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: 9.5, color: V4_T.muteSoft, marginTop: 2 }}>{sub}</div>}
    </div>
  )
}

// Table Header Component
function EnhancedTableHead({ classes, order, orderBy, onRequestSort }) {
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property)
  }

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            sortDirection={orderBy === headCell.id ? order : false}
            className={`${classes.tableCell} ${classes.headerCell}`}
            style={{ minWidth: headCell.width }}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  )
}

// ... (EnhancedTableHead remains the same)

// Main Component
function RoutingPropertiesTableInner(props) {
  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()
  const [state, dispatch] = useReducer(reducer, initialState)
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' })
  const [selectedProperty, setSelectedProperty] = useState(null)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [propertyOpportunities, setPropertyOpportunities] = useState({ loading: false, data: [], error: null })
  const [propertyIssues, setPropertyIssues] = useState({ loading: false, data: [], error: null })
  const [detailHistoryRange, setDetailHistoryRange] = useState('90')
  const [detailHistory, setDetailHistory] = useState({ loading: false, error: null, visits: [] })
  const [yardRoute, setYardRoute] = useState({ loading: false, error: null, data: null })
  const [propertyDetailTab, setPropertyDetailTab] = useState('overview')
  const [hoursView, setHoursView] = useState('visits')
  const [hoveredVisit, setHoveredVisit] = useState(null)
  const [pinnedVisit, setPinnedVisit] = useState(null)
  const [visitTypeFilter, setVisitTypeFilter] = useState('all')
  const [crewEfficiencyCurve, setCrewEfficiencyCurve] = useState(DEFAULT_CURVE)

  const territory = useTerritory()
  const { 
    crews: territoryCrews, 
    routes: territoryRoutes, 
    crewIds: territoryCrewIds, 
    routeIds: territoryRouteIds,
    loadingMetrics: territoryLoading,
    fetchCrews: fetchTerritoryContext
  } = territory

  useEffect(() => {
    const requestedView = new URLSearchParams(location.search).get('view')
    if (requestedView === 'map' || requestedView === 'table' || requestedView === 'routes') {
      dispatch({ type: 'SET_VIEW_MODE', payload: requestedView })
    }
    const requestedPropertyTab = new URLSearchParams(location.search).get('propertyTab')
    if (PROPERTY_DETAIL_TABS.some(tab => tab.value === requestedPropertyTab)) {
      setPropertyDetailTab(requestedPropertyTab)
    }
  }, [location.search])

  // Fetch crew-efficiency curve once; fall back to DEFAULT_CURVE silently
  useEffect(() => {
    let cancelled = false
    axios.get(`${props.url}/api/hmi/territory/config/crew-efficiency`)
      .then(response => {
        if (cancelled) return
        const factors = response.data?.factors
        if (factors && typeof factors === 'object') setCrewEfficiencyCurve(factors)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [props.url])

  // Fetch data from DB
  const fetchData = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true })
    try {
      const response = await axios.get(`${props.url}/api/aspire/routing/properties`)
      const rows = (response.data || []).map(normalizeRoutingRow)
      dispatch({ type: 'SET_ROWS', payload: rows })
    } catch (error) {
      console.error('Error fetching routing data:', error)
      dispatch({ type: 'SET_ERROR', payload: error.message })
    }
  }, [props.url])

  // Fetch sync status
  const fetchSyncStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${props.url}/api/aspire/routing/status`)
      dispatch({ type: 'SET_SYNC_STATUS', payload: response.data })
    } catch (error) {
      console.error('Error fetching sync status:', error)
    }
  }, [props.url])

  // Trigger sync from Aspire
  const handleSync = async () => {
    dispatch({ type: 'SET_SYNCING', payload: true })
    setSnackbar({ open: true, message: 'Syncing data from Aspire... This may take a few minutes.', severity: 'info' })

    try {
      await axios.post(`${props.url}/api/aspire/routing/sync`)
      // Poll for completion by checking if data has changed
      // For now just wait a bit and refresh
      setTimeout(async () => {
        await fetchData()
        await fetchSyncStatus()
        dispatch({ type: 'SET_SYNCING', payload: false })
        setSnackbar({ open: true, message: 'Sync complete! Data has been refreshed.', severity: 'success' })
      }, 5000)
    } catch (error) {
      console.error('Error syncing:', error)
      dispatch({ type: 'SET_SYNCING', payload: false })
      setSnackbar({ open: true, message: 'Sync failed: ' + error.message, severity: 'error' })
    }
  }

  useEffect(() => {
    fetchData()
    fetchSyncStatus()
  }, [fetchData, fetchSyncStatus])

  useEffect(() => {
    const propertyId = getAspirePropertyId(selectedProperty)
    if (!propertyId) {
      setDetailHistory({ loading: false, error: null, visits: [] })
      return
    }

    let cancelled = false
    const fetchDetailHistory = async () => {
      setDetailHistory(prev => ({ ...prev, loading: true, error: null }))
      try {
        const response = await axios.get(`${props.url}/api/aspire/routing/properties/${propertyId}/visit-history`, {
          params: { range: detailHistoryRange }
        })
        if (!cancelled) {
          let visits = response.data?.visits || []
          if (state.demoMode) {
            visits = anonymizeVisits(visits)
          }
          setDetailHistory({
            loading: false,
            error: null,
            visits
          })
        }
      } catch (error) {
        console.error('Error fetching property detail history:', error)
        if (!cancelled) {
          setDetailHistory({
            loading: false,
            error: error.message,
            visits: []
          })
        }
      }
    }

    fetchDetailHistory()
    return () => {
      cancelled = true
    }
  }, [props.url, selectedProperty, detailHistoryRange, state.demoMode])

  useEffect(() => {
    const propertyId = getAspirePropertyId(selectedProperty)
    if (!propertyId || !getPropertyCoordinates(selectedProperty)) {
      setYardRoute({ loading: false, error: null, data: null })
      return
    }

    let cancelled = false
    const fetchYardRoute = async () => {
      setYardRoute({ loading: true, error: null, data: null })
      try {
        const response = await axios.get(`${props.url}/api/aspire/routing/properties/${propertyId}/yard-route`)
        if (!cancelled) {
          setYardRoute({ loading: false, error: null, data: response.data || null })
        }
      } catch (error) {
        console.error('Error fetching yard route:', error)
        if (!cancelled) {
          setYardRoute({
            loading: false,
            error: error.response?.data?.message || error.response?.data?.error || error.message,
            data: null
          })
        }
      }
    }

    fetchYardRoute()
    return () => {
      cancelled = true
    }
  }, [props.url, selectedProperty])

  useEffect(() => {
    if (state.viewMode === 'map') {
      fetchTerritoryContext()
    }
  }, [fetchTerritoryContext, state.viewMode])

  // Apply filters
  useEffect(() => {
    let filtered = [...state.rows]

    // Text search
    if (state.searchText) {
      const search = state.searchText.toLowerCase()
      filtered = filtered.filter(row =>
        (row.name && row.name.toLowerCase().includes(search)) ||
        (row.address1 && row.address1.toLowerCase().includes(search)) ||
        (row.companyName && row.companyName.toLowerCase().includes(search)) ||
        (row.companyId && String(row.companyId).includes(search))
      )
    }

    if (state.companyFilter) {
      filtered = filtered.filter(row => String(row.companyId || '') === state.companyFilter)
    }

    // Quick filters
    if (state.showUnderBidOnly) {
      filtered = filtered.filter(row => row.onSiteVariance < 0)
    }
    if (state.showNegativeProfitOnly) {
      filtered = filtered.filter(row => row.netProfit < 0)
    }
    if (state.showOvertimeOnly) {
      filtered = filtered.filter(row => row.totalOTHours > 0)
    }
    if (state.showUnplacedOnly) {
      filtered = filtered.filter(row => Number(row.unplacedTicketCount) > 0)
    }
    if (state.showSyncProblemsOnly) {
      filtered = filtered.filter(row => row.syncHealth?.status === 'problem')
    }

    if (state.demoMode) {
      filtered = filtered.map(anonymizeProperty)
    }

    dispatch({ type: 'SET_FILTERED_ROWS', payload: filtered })
  }, [state.rows, state.searchText, state.companyFilter, state.showUnderBidOnly, state.showNegativeProfitOnly, state.showOvertimeOnly, state.showUnplacedOnly, state.showSyncProblemsOnly, state.demoMode])

  // Handlers
  const handleRequestSort = (event, property) => {
    const isAsc = state.orderBy === property && state.order === 'asc'
    dispatch({ type: 'SET_ORDER', payload: { order: isAsc ? 'desc' : 'asc', orderBy: property } })
  }

  const handleChangePage = (event, newPage) => {
    dispatch({ type: 'SET_PAGE', payload: newPage })
  }

  const handleChangeRowsPerPage = (event) => {
    dispatch({ type: 'SET_ROWS_PER_PAGE', payload: parseInt(event.target.value, 10) })
  }

  const handleSearchChange = (event) => {
    dispatch({ type: 'SET_SEARCH_TEXT', payload: event.target.value })
  }

  const handleCompanyFilterChange = (event) => {
    dispatch({ type: 'SET_COMPANY_FILTER', payload: event.target.value })
  }

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false })
  }

  const fetchPropertyOpportunities = useCallback(async (propertyId) => {
    if (!propertyId) return
    setPropertyOpportunities({ loading: true, data: [], error: null })
    try {
      const response = await axios.get(`${props.url}/api/aspire/routing/properties/${propertyId}/opportunities`)
      let data = Array.isArray(response.data) ? response.data : []
      if (state.demoMode) {
        data = anonymizeOpportunities(data)
      }
      setPropertyOpportunities({ loading: false, data, error: null })
    } catch (err) {
      console.error('Error fetching opportunities:', err)
      setPropertyOpportunities({ loading: false, data: [], error: err.message })
    }
  }, [props.url, state.demoMode])

  const fetchPropertyIssues = useCallback(async (propertyId) => {
    if (!propertyId) return
    setPropertyIssues({ loading: true, data: [], error: null })
    try {
      const response = await axios.get(`${props.url}/api/aspire/routing/properties/${propertyId}/issues`)
      let data = Array.isArray(response.data) ? response.data : []
      if (state.demoMode) {
        data = anonymizeIssues(data)
      }
      setPropertyIssues({ loading: false, data, error: null })
    } catch (err) {
      console.error('Error fetching issues:', err)
      setPropertyIssues({ loading: false, data: [], error: err.message })
    }
  }, [props.url, state.demoMode])

  const handleOpenPropertyDrawer = (row) => {
    setSelectedProperty(row)
    setHoveredVisit(null)
    setPinnedVisit(null)
    setVisitTypeFilter('all')
    const propertyId = row.propertyId || row.PropertyID
    if (propertyId) {
      fetchPropertyOpportunities(propertyId)
      fetchPropertyIssues(propertyId)
    }
  }

  const handleClosePropertyDrawer = () => {
    setSelectedProperty(null)
    setHoveredVisit(null)
    setPinnedVisit(null)
    setVisitTypeFilter('all')
  }

  const handlePropertyDetailTabChange = (tabValue) => {
    setPropertyDetailTab(tabValue)
    const params = new URLSearchParams(location.search)
    params.set('propertyTab', tabValue)
    history.replace({
      pathname: location.pathname,
      search: params.toString() ? `?${params.toString()}` : ''
    })
  }

  const handleRowKeyDown = (event, row) => {
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    handleOpenPropertyDrawer(row)
  }

  const handleMapSnackbar = useCallback((message, severity = 'info') => {
    setSnackbar({ open: true, message, severity })
  }, [])

  const companyOptions = useMemo(() => {
    const byId = new Map()
    state.rows.forEach(row => {
      const key = row.companyId ? String(row.companyId) : ''
      if (!key) return
      if (!byId.has(key)) {
        byId.set(key, {
          id: key,
          name: row.companyName || `Company #${key}`,
          label: `${row.companyName || 'Company'} #${key}`,
          count: 0
        })
      }
      byId.get(key).count += 1
    })
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name))
  }, [state.rows])

  // Calculate summary stats
  const totalProperties = state.filteredRows.length
  const problemProperties = state.filteredRows.filter(r => r.netProfit < 0).length
  const underBidProperties = state.filteredRows.filter(r => r.onSiteVariance < 0).length
  const unplacedRows = state.rows.filter(r => Number(r.unplacedTicketCount) > 0)
  const unplacedTicketTotal = unplacedRows.reduce((sum, r) => sum + Number(r.unplacedTicketCount || 0), 0)
  const syncProblemRows = state.filteredRows.filter(r => r.syncHealth?.status === 'problem')
  
  // Header should be MTC focused
  const mtcEarnedRevenue = state.filteredRows.reduce((sum, r) => sum + (r.maintenanceEarnedRevenue || r.totalEarnedRevenue || 0), 0)
  const mtcEstimatedRevenue = state.filteredRows.reduce((sum, r) => sum + (r.maintenanceEstimatedRevenue || r.totalEstimatedRevenue || 0), 0)
  const mtcProfit = state.filteredRows.reduce((sum, r) => sum + (r.netProfit || 0), 0)

  // Total view (still tracked for property health)
  const totalEarnedRevenue = state.filteredRows.reduce((sum, r) => sum + (r.totalEarnedRevenue || 0), 0)
  const totalEstimatedRevenue = state.filteredRows.reduce((sum, r) => sum + (r.totalEstimatedRevenue || 0), 0)
  const selectedCompany = state.companyFilter
    ? companyOptions.find(company => company.id === state.companyFilter)
    : null

  // Get value class based on positive/negative
  const getValueClass = (value, inverse = false) => {
    if (value == null || value === 0) return ''
    if (inverse) {
      return value > 0 ? classes.negative : classes.positive
    }
    return value < 0 ? classes.negative : classes.positive
  }

  // Check if row has problems
  const isRowProblematic = (row) => {
    return row.netProfit < 0 || row.onSiteVariance < -2 || row.profitMargin < -50
  }

  const renderDrawerRow = (label, value, valueClass = '') => (
    <div className={classes.drawerStatRow}>
      <span className={classes.drawerStatLabel}>{label}</span>
      <span className={`${classes.drawerStatValue} ${valueClass}`}>{value}</span>
    </div>
  )

  const renderServicePanel = (serviceDetail) => (
    <div className={classes.servicePanel}>
      <div className={classes.servicePanelTitle}>{serviceDetail.name} Details</div>
      {renderDrawerRow('Tickets', serviceDetail.tickets)}
      {renderDrawerRow('Completed', serviceDetail.completed)}
      {renderDrawerRow('Future', serviceDetail.future)}
      {renderDrawerRow('Hours', formatNumber(serviceDetail.hours))}
      {renderDrawerRow('Cost', formatCurrencyOrPending(serviceDetail.cost))}
    </div>
  )

  const renderTicketLink = (visit) => {
    const ticketLabel = getTicketLabel(visit)
    const ticketUrl = getAspireWorkTicketUrl(visit)
    if (!ticketLabel) return null
    if (!ticketUrl) return ticketLabel
    return (
      <MuiLink
        href={ticketUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(event) => event.stopPropagation()}
        style={{ fontWeight: 700 }}
      >
        {ticketLabel}
      </MuiLink>
    )
  }

  const renderDetailHistoryVisit = (visit, index) => {
    const ticketLink = renderTicketLink(visit)
    const clockEntries = visit.crewTimeEntries || []
    return (
      <div key={`${getWorkTicketId(visit) || visit.WorkTicketVisitID || index}`} className={classes.visitRow}>
        <Box display="flex" justifyContent="space-between" gridGap={12}>
          <strong>{formatVisitDateTime(visit)}</strong>
          <span>{formatNumber(getVisitHours(visit))} hrs</span>
        </Box>
        <div className={classes.visitMeta}>
          {[getVisitCrewName(visit), getVisitStatus(visit), visit.ServiceName || visit.ServiceDisplayName, ticketLink]
            .filter(Boolean)
            .map((item, itemIndex) => (
              <React.Fragment key={itemIndex}>
                {itemIndex > 0 && ' | '}
                {item}
              </React.Fragment>
            ))}
        </div>
        <div className={classes.visitMeta}>
          {[
            visit.RouteName && `Route: ${visit.RouteName}`,
            visit.ArrivalTime && `Arrival ${formatTimeOnly(visit.ArrivalTime)}`,
            visit.DepartureTime && `Departure ${formatTimeOnly(visit.DepartureTime)}`,
            visit.ScheduledDate && `Scheduled ${formatShortDateTime(visit.ScheduledDate)}`
          ].filter(Boolean).join(' | ')}
        </div>
        {clockEntries.length > 0 && (
          <div className={classes.clockEntryRow}>
            {clockEntries.slice(0, 5).map((entry, entryIndex) => (
              <Box key={entry.workTicketTimeID || entryIndex} display="flex" justifyContent="space-between" gridGap={10}>
                <span>{entry.contactName || 'Crew member'}</span>
                <span>
                  {formatTimeOnly(entry.startTime)}-{formatTimeOnly(entry.endTime)} · {formatNumber(entry.hours)} hrs{entry.isDriveTime ? ' drive' : ''}
                </span>
              </Box>
            ))}
            {clockEntries.length > 5 && (
              <div className={classes.visitMeta} style={{ marginTop: 4 }}>
                Showing 5 of {clockEntries.length} clock entries.
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  if (state.loading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
      </div>
    )
  }

  if (state.error) {
    return (
      <Paper className={classes.paper}>
        <Box p={3} textAlign="center">
          <ErrorIcon color="error" style={{ fontSize: 48 }} />
          <Typography color="error">Error loading data: {state.error}</Typography>
        </Box>
      </Paper>
    )
  }

  const sortedRows = stableSort(state.filteredRows, getComparator(state.order, state.orderBy))
  const paginatedRows = sortedRows.slice(
    state.page * state.rowsPerPage,
    state.page * state.rowsPerPage + state.rowsPerPage
  )
  const visitHistoryRows = getVisitHistoryRows(selectedProperty)
  const irrigationDetail = getServiceDetail(selectedProperty, 'Irrigation')
  const herbicideDetail = getServiceDetail(selectedProperty, 'Herbicide')
  const geoInsights = {
    ...buildGeoInsights(selectedProperty, state.rows),
    yardRoute: yardRoute.data,
    routeLoading: yardRoute.loading,
    routeError: yardRoute.error
  }
  const sidebarData = buildSidebarData(selectedProperty, detailHistory, geoInsights)
  const recentVisits = sidebarData.visitRows.filter(visit => visit.wAgo < 4)
  const priorVisits = sidebarData.visitRows.filter(visit => visit.wAgo >= 4 && visit.wAgo < 12)
  const recentAvg = recentVisits.length
    ? recentVisits.reduce((sum, visit) => sum + visit.hrs, 0) / recentVisits.length
    : sidebarData.kpis.avgHours
  const priorAvg = priorVisits.length
    ? priorVisits.reduce((sum, visit) => sum + visit.hrs, 0) / priorVisits.length
    : recentAvg
  const trendPct = priorAvg ? Math.round(((recentAvg - priorAvg) / priorAvg) * 100) : 0
  const marginSpark = sidebarData.monthlyRows.map(month => month.rev + month.othRev - month.cost)
  const visitsSpark = sidebarData.monthlyRows.map(month => month.visits)
  const dayAbbrev = getDayAbbrev(sidebarData.scheduledDay)
  const dominantService = sidebarData.serviceRows
    .slice()
    .sort((a, b) => b.hours - a.hours)[0]

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <div className={classes.toolbar}>
          <div style={{ width: '100%', padding: '16px 20px 0' }}>
            <RoutingBreadcrumbs />
            {/* Title and Sync Button */}
            <div className={classes.titleRow}>
              <Box display="flex" alignItems="center">
                <Typography className={classes.title} variant="h6">
                  Routing Analysis - Property Performance
                </Typography>
                <Tabs
                  value={state.viewMode}
                  onChange={(e, v) => dispatch({ type: 'SET_VIEW_MODE', payload: v })}
                  indicatorColor="primary"
                  textColor="primary"
                  style={{ marginLeft: 32 }}
                >
                  <Tab value="table" icon={<TableChartIcon style={{ fontSize: 18 }} />} label="Table" />
                  <Tab value="map" icon={<MapIcon style={{ fontSize: 18 }} />} label="Map" />
                  <Tab value="routes" icon={<EventNoteIcon style={{ fontSize: 18 }} />} label="Routes" />
                </Tabs>
              </Box>
              <div className={classes.syncInfo} style={{ marginLeft: 'auto' }}>
                <Typography className={classes.lastSync}>
                  Last synced: {formatDate(state.syncStatus?.lastSyncedAt)}
                </Typography>
                <button
                  className="btn btn-ghost"
                  onClick={() => history.push('/routing/schedule')}
                  style={{ marginLeft: 12, fontSize: 12, gap: 6 }}
                >
                  <EventNoteIcon style={{ fontSize: 16 }} />
                  View Schedule
                </button>
                {/* <button
                  className="btn btn-ghost"
                  onClick={() => dispatch({ type: 'SET_DEMO_MODE', payload: !state.demoMode })}
                  style={{ 
                    marginLeft: 6, 
                    fontSize: 12, 
                    gap: 6,
                    color: state.demoMode ? V4_T.risk : 'inherit',
                    background: state.demoMode ? 'rgba(239, 68, 68, 0.1)' : 'transparent'
                  }}
                >
                  {state.demoMode ? <VisibilityOffIcon style={{ fontSize: 16 }} /> : <VisibilityIcon style={{ fontSize: 16 }} />}
                  {state.demoMode ? 'Demo Mode: ON' : 'Demo Mode'}
                </button> */}
                <button
                  className="btn btn-ghost"
                  onClick={() => history.push('/routing/territory/dashboard')}
                  style={{ marginLeft: 6, fontSize: 12, gap: 6 }}
                >
                  <LockIcon style={{ fontSize: 16 }} />
                  Scenario Dashboard
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSync}
                  disabled={state.syncing}
                  style={{ marginLeft: 16, fontSize: 12, gap: 6 }}
                >
                  {state.syncing ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <SyncIcon style={{ fontSize: 16 }} />
                  )}
                  {state.syncing ? 'Syncing...' : 'Sync Aspire Data'}
                </button>
              </div>
            </div>

            {/* Summary Stats - Table view only */}
            {state.viewMode === 'table' && (
              <>
                {state.demoMode && (
                  <Box mb={2}>
                    <Alert severity="warning" variant="filled" style={{ borderRadius: 8 }}>
                      <strong>Demo Mode Active:</strong> All customer names, addresses, and contact info are currently masked with fake data. Underlying metrics remain real.
                    </Alert>
                  </Box>
                )}
                <div className={classes.statsWrapper}>
                  <div className={classes.statsRow}>
                    <div className={classes.statTile}>
                      <span className={`${classes.statAccent} ${classes.statAccentPrimary}`} />
                      <div className={classes.statValue}>{totalProperties}</div>
                      <div className={classes.statLabel}>Properties</div>
                    </div>
                    <div className={classes.statTile}>
                      <span className={`${classes.statAccent} ${classes.statAccentDanger}`} />
                      <div className={`${classes.statValue} ${classes.negative}`}>{problemProperties}</div>
                      <div className={classes.statLabel}>Losing Money</div>
                    </div>
                    <div className={classes.statTile}>
                      <span className={`${classes.statAccent} ${classes.statAccentWarn}`} />
                      <div className={`${classes.statValue} ${classes.warning}`}>{underBidProperties}</div>
                      <div className={classes.statLabel}>Under-Bid</div>
                    </div>
                    <div
                      className={classes.statTile}
                      role="button"
                      tabIndex={0}
                      onClick={() => dispatch({ type: 'TOGGLE_UNPLACED' })}
                      onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); dispatch({ type: 'TOGGLE_UNPLACED' }) } }}
                      title="Upcoming maintenance tickets that are NOT on Aspire's schedule board — they won't appear on the crew's mobile app. Click to filter the table."
                      style={{
                        cursor: 'pointer',
                        outline: state.showUnplacedOnly ? '2px solid #8c5b00' : 'none'
                      }}
                    >
                      <span className={`${classes.statAccent} ${classes.statAccentWarn}`} />
                      <div className={`${classes.statValue} ${classes.warning}`}>{unplacedTicketTotal}</div>
                      <div className={classes.statLabel}>Unscheduled Tickets</div>
                      <div className={classes.statHint}>
                        {unplacedRows.length} propert{unplacedRows.length === 1 ? 'y' : 'ies'} · not on Aspire board{state.showUnplacedOnly ? ' · filtering' : ' · click to filter'}
                      </div>
                    </div>
                    <div
                      className={classes.statTile}
                      role="button"
                      tabIndex={0}
                      onClick={() => dispatch({ type: 'TOGGLE_SYNC_PROBLEMS' })}
                      onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); dispatch({ type: 'TOGGLE_SYNC_PROBLEMS' }) } }}
                      title="Properties whose last sync flagged data problems (estimate, board visits, geo, crew). Click to filter."
                      style={{
                        cursor: 'pointer',
                        outline: state.showSyncProblemsOnly ? '2px solid #7a4fb3' : 'none'
                      }}
                    >
                      <span className={`${classes.statAccent} ${classes.statAccentWarn}`} />
                      <div className={`${classes.statValue} ${classes.warning}`}>{syncProblemRows.length}</div>
                      <div className={classes.statLabel}>Sync Problems</div>
                      <div className={classes.statHint}>
                        {state.showSyncProblemsOnly ? 'filtering' : 'click to filter'}
                      </div>
                    </div>
                  </div>
                  <div className={classes.statsRow}>
                    <div className={`${classes.statTile} ${classes.statTileMoney}`}>
                      <span className={`${classes.statAccent} ${classes.statAccentPrimary}`} />
                      <div className={`${classes.statValue} ${classes.statValueMoney}`}>{formatCurrency(mtcEstimatedRevenue)}</div>
                      <div className={classes.statLabel}>Mtc Est. Revenue</div>
                    </div>
                    <div className={`${classes.statTile} ${classes.statTileMoney}`}>
                      <span className={`${classes.statAccent} ${classes.statAccentPrimary}`} />
                      <div className={`${classes.statValue} ${classes.statValueMoney}`}>{formatCurrency(mtcEarnedRevenue)}</div>
                      <div className={classes.statLabel}>Mtc Earned Revenue</div>
                      {mtcEstimatedRevenue > 0 && (
                        <div className={classes.statHint}>
                          {((mtcEarnedRevenue / mtcEstimatedRevenue) * 100).toFixed(1)}% of est. · {formatCurrency(totalEarnedRevenue)} total earned
                        </div>
                      )}
                    </div>
                    <div className={`${classes.statTile} ${classes.statTileMoney}`}>
                      <span className={`${classes.statAccent} ${mtcProfit < 0 ? classes.statAccentDanger : classes.statAccentOk}`} />
                      <div className={`${classes.statValue} ${classes.statValueMoney} ${mtcProfit < 0 ? classes.negative : classes.positive}`}>
                        {formatCurrency(mtcProfit)}
                      </div>
                      <div className={classes.statLabel}>Mtc Profit</div>
                    </div>
                  </div>
                </div>

                {/* Search and Filters */}
                <div className={classes.searchContainer} style={{ marginBottom: 12 }}>
                  <div className={classes.searchFieldHalf}>
                    <DFSearchInput
                      value={state.searchText}
                      onChange={handleSearchChange}
                      placeholder="Search properties by name or address..."
                      variant="prominent"
                    />
                  </div>
                  <Autocomplete
                    size="small"
                    options={companyOptions}
                    value={companyOptions.find(company => company.id === state.companyFilter) || null}
                    onChange={(event, option) => handleCompanyFilterChange({ target: { value: option?.id || '' } })}
                    getOptionLabel={(option) => option ? `${option.label} (${option.count})` : ''}
                    getOptionSelected={(option, value) => option.id === value.id}
                    className={classes.companyFilter}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Filter by company..."
                      />
                    )}
                  />
                  <Tooltip title="Refresh from database">
                    <IconButton onClick={fetchData} className={classes.refreshButton}>
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {selectedCompany && (
                    <Chip
                      label={`${selectedCompany.label} • ${totalProperties} properties`}
                      onDelete={() => dispatch({ type: 'SET_COMPANY_FILTER', payload: '' })}
                      className={classes.activeFilterChip}
                      variant="outlined"
                      size="small"
                    />
                  )}
                </div>

                {/* Quick Filter Chips */}
                <div className={classes.filterChips}>
                  <DFFilterChip
                    label="Under-Bid Properties"
                    active={state.showUnderBidOnly}
                    onClick={() => dispatch({ type: 'TOGGLE_UNDER_BID' })}
                  />
                  <DFFilterChip
                    label="Negative Profit"
                    active={state.showNegativeProfitOnly}
                    onClick={() => dispatch({ type: 'TOGGLE_NEGATIVE_PROFIT' })}
                  />
                  <DFFilterChip
                    label="Has Overtime"
                    active={state.showOvertimeOnly}
                    onClick={() => dispatch({ type: 'TOGGLE_OVERTIME' })}
                  />
                  <Typography variant="body2" color="textSecondary" style={{ marginLeft: 'auto', fontWeight: 600 }}>
                    {state.filteredRows.length} / {state.rows.length} properties
                  </Typography>
                </div>
              </>
            )}
          </div>
        </div>

        {/* View Mode Content */}
        {state.viewMode === 'map' ? (
          <Box p={2}>
            {territoryLoading ? (
              <div className={classes.loadingContainer}>
                <CircularProgress />
              </div>
            ) : (
              <MapTab
                url={props.url}
                selectedScenarioId={null}
                crews={territoryCrews}
                crewIds={territoryCrewIds}
                routes={territoryRoutes}
                routeIds={territoryRouteIds}
                onSnackbar={handleMapSnackbar}
              />
            )}
          </Box>
        ) : state.viewMode === 'routes' ? (
          <Box p={2}>
            <RoutesTab
              url={props.url}
              onSnackbar={handleMapSnackbar}
              onRoutesChanged={() => {
                fetchTerritoryContext()
              }}
            />
          </Box>
        ) : (
          <>
            {/* Empty State */}
            {state.rows.length === 0 ? (
              <div className={classes.emptyState}>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  No property data yet
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Click "Sync Aspire Data" to fetch property performance metrics from Aspire.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={state.syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                  onClick={handleSync}
                  disabled={state.syncing}
                >
                  {state.syncing ? 'Syncing...' : 'Sync Aspire Data'}
                </Button>
              </div>
            ) : (
              <>
                <div className={classes.tableWrapper}>
                  <Table className={classes.table} size="small">
                    <EnhancedTableHead
                      classes={classes}
                      order={state.order}
                      orderBy={state.orderBy}
                      onRequestSort={handleRequestSort}
                    />
                    <TableBody>
                      {paginatedRows.map((row, index) => (
                        <TableRow
                          key={row.propertyId || row._id || index}
                          hover
                          tabIndex={0}
                          role="button"
                          onClick={() => handleOpenPropertyDrawer(row)}
                          onKeyDown={(event) => handleRowKeyDown(event, row)}
                          className={`${classes.clickableRow} ${isRowProblematic(row) ? classes.problemRow : ''}`}
                        >
                          <TableCell className={classes.tableCell}>
                            <Box display="flex" alignItems="center" gap={1}>
                              {isRowProblematic(row) && (
                                <Tooltip title="Problem property - review pricing">
                                  <WarningIcon fontSize="small" color="error" />
                                </Tooltip>
                              )}
                              {Number(row.unplacedTicketCount) > 0 && (
                                <Tooltip title={`${row.unplacedTicketCount} upcoming ticket${row.unplacedTicketCount > 1 ? 's' : ''} NOT on Aspire's schedule board${row.nextUnplacedDate ? ` (first: ${row.nextUnplacedDate})` : ''} — won't appear on the crew's mobile app until placed`}>
                                  <WarningIcon fontSize="small" style={{ color: '#8c5b00' }} />
                                </Tooltip>
                              )}
                              {row.syncHealth?.status === 'problem' && (
                                <Tooltip title={`Sync problems: ${(row.syncHealth.issues || []).map(code => SYNC_ISSUE_LABELS[code] || code).join(' · ')}`}>
                                  <WarningIcon fontSize="small" style={{ color: '#7a4fb3' }} />
                                </Tooltip>
                              )}
                              {getAspirePropertyUrl(row) ? (
                                <MuiLink
                                  href={getAspirePropertyUrl(row)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(event) => event.stopPropagation()}
                                  style={{ color: 'var(--df-info, #3454c1)', fontWeight: 600 }}
                                >
                                  {row.name || '-'}
                                </MuiLink>
                              ) : (
                                row.name || '-'
                              )}
                            </Box>
                          </TableCell>
                          <TableCell className={classes.tableCell}>
                            {row.companyId
                              ? `${row.companyName || 'Company'} #${row.companyId}`
                              : '-'}
                          </TableCell>
                          <TableCell className={classes.tableCell}>{row.address1 || '-'}</TableCell>
                          <TableCell className={classes.tableCell} align="right">
                            <Tooltip title={row.completedTotalVisits !== row.completedMtcVisits ? `MTC: ${row.completedMtcVisits} · Irrigation: ${row.irrigationCompletedVisits} · Herbicide: ${row.herbicideCompletedVisits}` : ""}>
                              <span style={{ cursor: row.completedTotalVisits !== row.completedMtcVisits ? 'help' : 'default', borderBottom: row.completedTotalVisits !== row.completedMtcVisits ? '1px dotted' : 'none' }}>
                                {formatVisitSplit(row.completedMtcVisits, row.completedTotalVisits)}
                              </span>
                            </Tooltip>
                          </TableCell>
                          <TableCell className={classes.tableCell} align="right">
                            <Tooltip title={[
                              row.annualTotalVisits !== row.annualVisitsDisplay ? `MTC: ${row.annualVisitsDisplay} · Irrigation: ${row.irrigationAnnualVisits} · Herbicide: ${row.herbicideAnnualVisits}` : null,
                              row.annualTicketCount && row.annualTicketCount !== row.annualVisitsDisplay ? `Synced ticket count: ${row.annualTicketCount}` : null
                            ].filter(Boolean).join(' · ')}>
                              <span style={{ cursor: row.annualTotalVisits !== row.annualVisitsDisplay || (row.annualTicketCount && row.annualTicketCount !== row.annualVisitsDisplay) ? 'help' : 'default', borderBottom: row.annualTotalVisits !== row.annualVisitsDisplay || (row.annualTicketCount && row.annualTicketCount !== row.annualVisitsDisplay) ? '1px dotted' : 'none' }}>
                                {formatVisitSplit(row.annualVisitsDisplay, row.annualTotalVisits)}
                              </span>
                            </Tooltip>
                          </TableCell>
                          <TableCell className={classes.tableCell} align="right">{formatNumber(row.estimatedHours)}</TableCell>
                          <TableCell className={classes.tableCell} align="right">
                            <Tooltip title={row.internalEstimateSource && row.internalEstimateSource !== 'aspire'
                              ? `Source: ${row.internalEstimateSource}${row.internalEstimateVisitCount ? ` · ${row.internalEstimateVisitCount} visit${row.internalEstimateVisitCount === 1 ? '' : 's'} (trailing 12 mo)` : ''}`
                              : ''}>
                              <span style={{ cursor: row.internalEstimateSource && row.internalEstimateSource !== 'aspire' ? 'help' : 'default', borderBottom: row.internalEstimateSource && row.internalEstimateSource !== 'aspire' ? '1px dotted' : 'none' }}>
                                {formatNumber(row.internalEstHoursDisplay)}
                              </span>
                            </Tooltip>
                          </TableCell>
                          <TableCell className={classes.tableCell} align="right">{formatNumber(row.avgOnSiteHoursDisplay)}</TableCell>
                          <TableCell className={classes.tableCell} align="right">{formatNumber(row.medianOnSiteHoursDisplay)}</TableCell>
                          <TableCell className={classes.tableCell} align="right">
                            <span className={getValueClass(row.onSiteVariance)}>
                              {formatNumber(row.onSiteVariance)}
                            </span>
                          </TableCell>
                          <TableCell className={classes.tableCell} align="right">
                            <span className={row.totalOTHours > 0 ? classes.warning : ''}>
                              {formatNumber(row.totalOTHours)}
                            </span>
                          </TableCell>
                          <TableCell className={classes.tableCell} align="right">{formatCurrency(row.estimatedLaborCost)}</TableCell>
                          <TableCell className={classes.tableCell} align="right">{formatCurrency(row.totalLaborCost)}</TableCell>
                          <TableCell className={classes.tableCell} align="right">
                            <span className={getValueClass(row.costVariance)}>
                              {formatCurrency(row.costVariance)}
                            </span>
                          </TableCell>
                          <TableCell className={classes.tableCell} align="right">{formatCurrency(row.totalEstimatedRevenue)}</TableCell>
                          <TableCell className={classes.tableCell} align="right">{formatCurrency(row.totalEarnedRevenue)}</TableCell>
                          <TableCell className={classes.tableCell} align="right">
                            <span className={getValueClass(row.netProfit)}>
                              {formatCurrency(row.netProfit)}
                            </span>
                          </TableCell>
                          <TableCell className={classes.tableCell} align="right">
                            <span className={getValueClass(row.profitMargin)}>
                              {formatPercent(row.profitMargin)}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                      {paginatedRows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={headCells.length} align="center">
                            <Typography color="textSecondary">No properties match your filters</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>

                <TablePagination
                  rowsPerPageOptions={[10, 20, 50, 100]}
                  component="div"
                  count={state.filteredRows.length}
                  rowsPerPage={state.rowsPerPage}
                  page={state.page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </>
            )}
          </>
        )}
      </Paper>

      <PropertyDetailDrawer
        open={!!selectedProperty}
        onClose={handleClosePropertyDrawer}
        classes={classes}
        selectedProperty={selectedProperty}
        sidebarData={sidebarData}
        geoInsights={geoInsights}
        activeTab={propertyDetailTab}
        onTabChange={handlePropertyDetailTabChange}
        hoursView={hoursView}
        setHoursView={setHoursView}
        detailHistoryRange={detailHistoryRange}
        setDetailHistoryRange={setDetailHistoryRange}
        detailHistory={detailHistory}
        hoveredVisit={hoveredVisit}
        setHoveredVisit={setHoveredVisit}
        pinnedVisit={pinnedVisit}
        setPinnedVisit={setPinnedVisit}
        visitTypeFilter={visitTypeFilter}
        setVisitTypeFilter={setVisitTypeFilter}
        trendPct={trendPct}
        marginSpark={marginSpark}
        visitsSpark={visitsSpark}
        dayAbbrev={dayAbbrev}
        dominantService={dominantService}
        propertyOpportunities={propertyOpportunities}
        isProblematic={!!selectedProperty && isRowProblematic(selectedProperty)}
        onEditProperty={() => setEditModalOpen(true)}
        renderTicketLink={renderTicketLink}
      />

      <PropertyEditModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        property={selectedProperty}
        crews={territoryCrews}
        onSave={(payload) => {
          // UI-only for now — persistence wired up in a later pass.
          console.log('[PropertyEdit] save payload (not yet persisted):', payload)
          setSnackbar({ open: true, message: 'Property details captured (not yet saved to server).', severity: 'info' })
        }}
      />

      {/* Property detail drawer */}
      <Drawer
        anchor="right"
        open={false}
        onClose={handleClosePropertyDrawer}
      >
        <div className={classes.drawer}>
          <div className={classes.drawerHeader}>
            <div>
              <Typography variant="h6" className={classes.drawerTitle}>
                {selectedProperty?.name || 'Property Details'}
              </Typography>
              {selectedProperty?.address1 && (
                <Typography variant="body2" className={classes.drawerSubtitle}>
                  {selectedProperty.address1}
                </Typography>
              )}
              <Box display="flex" flexWrap="wrap" gridGap={6} mt={1}>
                {getAspirePropertyId(selectedProperty) && (
                  <Chip
                    size="small"
                    label={`Aspire #${getAspirePropertyId(selectedProperty)}`}
                    style={{ backgroundColor: 'rgba(255,255,255,0.18)', color: '#fff' }}
                  />
                )}
                {selectedProperty?.companyId && (
                  <Chip
                    size="small"
                    label={`${selectedProperty.companyName || 'Company'} #${selectedProperty.companyId}`}
                    style={{ backgroundColor: 'rgba(255,255,255,0.18)', color: '#fff' }}
                  />
                )}
              </Box>
            </div>
            <IconButton size="small" onClick={handleClosePropertyDrawer} style={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </div>

          <div className={classes.drawerContent}>
            {selectedProperty && isRowProblematic(selectedProperty) && (
              <div className={classes.drawerAlert}>
                <WarningIcon fontSize="small" />
                Review pricing or route assumptions for this property.
              </div>
            )}

            <div className={classes.drawerActions}>
              {getAspirePropertyUrl(selectedProperty) && (
                <Button
                  variant="outlined"
                  size="small"
                  href={getAspirePropertyUrl(selectedProperty)}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<OpenInNewIcon />}
                >
                  Open in Aspire
                </Button>
              )}
            </div>

            <div className={classes.drawerSection}>
              <Typography className={classes.drawerSectionTitle}>Geographic Context</Typography>
              {geoInsights.hasCoordinates ? (
                <>
                  <MiniRouteMap property={selectedProperty} geoInsights={geoInsights} classes={classes} />
                  {renderDrawerRow(
                    geoInsights.yardRoute ? 'Yard to Site' : 'Yard to Site Estimate',
                    geoInsights.yardRoute
                      ? `${formatMiles(geoInsights.yardRoute.distanceMiles)} / ${formatMinutes(geoInsights.yardRoute.durationMinutes)}`
                      : `${formatMiles(geoInsights.yardRoadMiles)} / ${formatMinutes(geoInsights.yardDriveMinutes)}`
                  )}
                  {renderDrawerRow('Straight-Line Distance', formatMiles(geoInsights.yardStraightMiles))}
                  {renderDrawerRow(
                    'Nearest Customer',
                    geoInsights.nearestSite
                      ? `${geoInsights.nearestSite.name || 'Property'} (${formatMiles(geoInsights.nearestSite.distanceMiles)})`
                      : 'None found'
                  )}
                  {renderDrawerRow('Sites Within 1 mi', geoInsights.sitesWithinOneMile.length)}
                  {renderDrawerRow('Sites Within 3 mi', geoInsights.sitesWithinThreeMiles.length)}
                  {renderDrawerRow('Sites Within 5 mi', geoInsights.sitesWithinFiveMiles.length)}
                  {renderDrawerRow('Avg Nearby Distance', formatMiles(geoInsights.avgDistanceWithinFiveMiles))}
                  {renderDrawerRow('Median Nearby Distance', formatMiles(geoInsights.medianDistanceWithinFiveMiles))}
                </>
              ) : (
                <div className={classes.emptyDrawerNote}>
                  No coordinates available for nearby-site or yard travel estimates.
                </div>
              )}
            </div>

            <Divider style={{ margin: '16px 0' }} />

            <div className={classes.drawerSection}>
              <Typography className={classes.drawerSectionTitle}>Visit History</Typography>
              {renderDrawerRow('Completed Visits', formatVisitSplit(selectedProperty?.completedMtcVisits || selectedProperty?.completedOccurrences, selectedProperty?.completedTotalVisits || selectedProperty?.completedOccurrences))}
              {renderDrawerRow('Annual Visits', formatVisitSplit(selectedProperty?.annualVisitsDisplay || selectedProperty?.targetAnnualVisits || selectedProperty?.annualOccurrences, selectedProperty?.annualTotalVisits || selectedProperty?.annualVisitsDisplay || selectedProperty?.targetAnnualVisits || selectedProperty?.annualOccurrences))}
              {renderDrawerRow('Future Visits', selectedProperty?.futureVisits?.length || selectedProperty?.futureTicketCount || 0)}
              {selectedProperty?.crewLeaders?.length > 0 && (
                <Box display="flex" gridGap={6} flexWrap="wrap" mt={1}>
                  {selectedProperty.crewLeaders.map(crew => (
                    <Chip key={crew.id || crew.name} label={getCrewLabel(crew)} size="small" />
                  ))}
                </Box>
              )}
              <Box mt={1.5}>
                {visitHistoryRows.length > 0 ? (
                  visitHistoryRows.slice(0, 8).map((visit, index) => (
                    <div key={`${visit.workTicketId || visit.workTicketNumber || getVisitDateValue(visit) || index}`} className={classes.visitRow}>
                      <Box display="flex" justifyContent="space-between" gridGap={12}>
                        <strong>{getVisitCrewName(visit)}</strong>
                        <span>{formatNumber(getFirstPresent(visit, ['onSiteHours', 'actualHours', 'hours', 'HoursAct', 'estimatedHours']))} hrs</span>
                      </Box>
                      <div className={classes.visitMeta}>
                        {[formatVisitDateTime(visit), visit.statusName, visit.workTicketNumber && `Ticket ${visit.workTicketNumber}`]
                          .filter(Boolean)
                          .join(' | ')}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className={classes.emptyDrawerNote}>
                    Visit-level history is not in the current property payload yet. Completed visit rows will show crew, date/time, ticket, status, and hours here once synced.
                  </div>
                )}
                {visitHistoryRows.length > 8 && (
                  <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 8 }}>
                    Showing 8 of {visitHistoryRows.length} historical visits.
                  </Typography>
                )}
              </Box>
            </div>

            <Divider style={{ margin: '16px 0' }} />

            <div className={classes.drawerSection}>
              <Typography className={classes.drawerSectionTitle}>Aspire Opportunities</Typography>
              {propertyOpportunities.loading ? (
                <Box display="flex" justifyContent="center" p={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : propertyOpportunities.error ? (
                <Typography color="error" variant="body2">Error: {propertyOpportunities.error}</Typography>
              ) : propertyOpportunities.data.length > 0 ? (
                propertyOpportunities.data.map(opp => (
                  <div key={opp.OpportunityID} className={classes.visitRow} style={{ borderLeft: '3px solid var(--df-primary, #1f5e3a)', paddingLeft: 10, marginBottom: 12 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <strong style={{ fontSize: '0.95rem' }}>{opp.OpportunityName}</strong>
                      <Chip
                        size="small"
                        label={opp.OpportunityStatusName}
                        style={{
                          height: 20,
                          fontSize: '0.7rem',
                          backgroundColor: opp.OpportunityStatusID === 15 ? '#e8f5e9' : '#eee'
                        }}
                      />
                    </Box>
                    <div className={classes.visitMeta} style={{ marginTop: 4 }}>
                      #{opp.OpportunityNumber} | {opp.DivisionName} | {opp.InvoiceType}
                    </div>
                    <Box display="flex" justifyContent="space-between" mt={1}>
                      <div className={classes.statItem}>
                        <div style={{ fontSize: '0.7rem', color: '#666' }}>WON DATE</div>
                        <div style={{ fontWeight: 600 }}>{opp.WonDate ? new Date(opp.WonDate).toLocaleDateString() : '-'}</div>
                      </div>
                      <div className={classes.statItem} style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '0.7rem', color: '#666' }}>WON DOLLARS</div>
                        <div style={{ fontWeight: 700, color: '#1f5e3a' }}>{formatCurrency(opp.WonDollars)}</div>
                      </div>
                    </Box>
                    <Box display="flex" justifyContent="space-between" mt={1}>
                      <div className={classes.statItem}>
                        <div style={{ fontSize: '0.7rem', color: '#666' }}>EST. HOURS</div>
                        <div style={{ fontWeight: 600 }}>{formatNumber(opp.EstimatedLaborHours)}</div>
                      </div>
                      <div className={classes.statItem} style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '0.7rem', color: '#666' }}>MARGIN %</div>
                        <div style={{ fontWeight: 600, color: opp.EstimatedGrossMarginPercent < 40 ? '#a9341d' : 'inherit' }}>
                          {formatPercent(opp.EstimatedGrossMarginPercent)}
                        </div>
                      </div>
                    </Box>
                    {opp.SalesRepContactName && (
                      <div className={classes.visitMeta} style={{ marginTop: 6 }}>
                        Rep: {opp.SalesRepContactName}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className={classes.emptyDrawerNote}>No linked opportunities found.</div>
              )}
            </div>

            <Divider style={{ margin: '16px 0' }} />

            <div className={classes.drawerSection}>
              <Typography className={classes.drawerSectionTitle}>Aspire Issues</Typography>
              {propertyIssues.loading ? (
                <Box display="flex" justifyContent="center" p={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : propertyIssues.error ? (
                <Typography color="error" variant="body2">Error: {propertyIssues.error}</Typography>
              ) : propertyIssues.data.length > 0 ? (
                propertyIssues.data.map(issue => (
                  <div key={issue.IssueID} className={classes.visitRow} style={{ borderLeft: `3px solid ${issue.IssueStatusID === 3 ? '#eee' : '#ff9800'}`, paddingLeft: 10, marginBottom: 12 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <strong style={{ fontSize: '0.9rem' }}>{issue.IssueTypeName}</strong>
                      <Chip 
                        size="small" 
                        label={issue.IssueStatusName} 
                        style={{ 
                          height: 18, 
                          fontSize: '0.65rem', 
                          backgroundColor: issue.IssueStatusID === 3 ? '#e8f5e9' : '#fff3e0'
                        }} 
                      />
                    </Box>
                    <div style={{ fontSize: '0.8rem', marginTop: 4, whiteSpace: 'pre-wrap' }}>{issue.Description}</div>
                    <div className={classes.visitMeta} style={{ marginTop: 6 }}>
                      {issue.CreatedDateTime ? new Date(issue.CreatedDateTime).toLocaleDateString() : ''} | Priority: {issue.IssuePriorityName}
                    </div>
                  </div>
                ))
              ) : (
                <div className={classes.emptyDrawerNote}>No recent issues found.</div>
              )}
            </div>

            <Divider style={{ margin: '16px 0' }} />

            <div className={classes.drawerSection}>
              <Box display="flex" justifyContent="space-between" alignItems="center" gridGap={12} mb={1}>
                <Typography className={classes.drawerSectionTitle} style={{ marginBottom: 0 }}>
                  Details History
                </Typography>
                {detailHistory.loading && <CircularProgress size={16} />}
              </Box>
              <div className={classes.historyFilterRow}>
                {HISTORY_RANGE_OPTIONS.map(option => (
                  <Chip
                    key={option.value}
                    label={option.label}
                    size="small"
                    clickable
                    color={detailHistoryRange === option.value ? 'primary' : 'default'}
                    variant={detailHistoryRange === option.value ? 'default' : 'outlined'}
                    onClick={() => setDetailHistoryRange(option.value)}
                  />
                ))}
              </div>
              {detailHistory.error ? (
                <div className={classes.emptyDrawerNote}>
                  Could not load work-ticket visit history: {detailHistory.error}
                </div>
              ) : detailHistory.loading && detailHistory.visits.length === 0 ? (
                <div className={classes.emptyDrawerNote}>
                  Loading synced work-ticket visit data...
                </div>
              ) : detailHistory.visits.length > 0 ? (
                <>
                  {detailHistory.visits.slice(0, 12).map(renderDetailHistoryVisit)}
                  {detailHistory.visits.length > 12 && (
                    <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 8 }}>
                      Showing 12 of {detailHistory.visits.length} visit records.
                    </Typography>
                  )}
                </>
              ) : (
                <div className={classes.emptyDrawerNote}>
                  No synced work-ticket visit rows found for this range. Try a longer range or run the Aspire work-ticket visit sync.
                </div>
              )}
            </div>

            <Divider style={{ margin: '16px 0' }} />

            <div className={classes.drawerSection}>
              <Typography className={classes.drawerSectionTitle}>Service Details</Typography>
              <div className={classes.serviceGrid}>
                {renderServicePanel(irrigationDetail)}
                {renderServicePanel(herbicideDetail)}
              </div>
            </div>

            <Divider style={{ margin: '16px 0' }} />

            <div className={classes.drawerSection}>
              <Typography className={classes.drawerSectionTitle}>Hours Analysis</Typography>
              {renderDrawerRow('Est. Hours/Visit', formatNumber(selectedProperty?.estimatedHours))}
              {renderDrawerRow('Avg On-Site Hours', formatNumber(selectedProperty?.avgOnSiteHoursDisplay))}
              {renderDrawerRow('Median On-Site Hours', formatNumber(selectedProperty?.medianOnSiteHoursDisplay))}
              {renderDrawerRow('Est - Avg Variance', formatNumber(selectedProperty?.onSiteVariance), getValueClass(selectedProperty?.onSiteVariance))}
              {renderDrawerRow('Total OT Hours', formatNumber(selectedProperty?.totalOTHours), selectedProperty?.totalOTHours > 0 ? classes.warning : '')}
              {renderDrawerRow('Internal Est (man-hrs)', `${formatNumber(selectedProperty?.internalEstimatedManHours)} mh ÷ ${formatNumber(selectedProperty?.internalEstimateCrewCount, 1)} crew`)}
              {Number(selectedProperty?.internalEstimatedManHours) > 0 && (() => {
                const manHours = Number(selectedProperty?.internalEstimatedManHours)
                const currentCrew = selectedProperty?.assignedRouteCrewSize ||
                  Math.round(selectedProperty?.internalEstimateCrewCount) || 3
                return (
                  <div style={{ marginTop: 4 }}>
                    <Typography variant="caption" style={{ opacity: 0.6, display: 'block' }}>
                      Time by crew size
                    </Typography>
                    {[1, 2, 3, 4, 5].map(crewSize => {
                      const minutes = wallClockMinutes(manHours * 60, crewSize, crewEfficiencyCurve)
                      const isCurrent = crewSize === currentCrew
                      return (
                        <React.Fragment key={crewSize}>
                          {renderDrawerRow(
                            `@ ${crewSize} ${crewSize === 1 ? 'man' : 'men'}`,
                            <span style={isCurrent ? { fontWeight: 700 } : undefined}>
                              {formatNumber(minutes / 60, 2)}h
                              {isCurrent && (
                                <span style={{
                                  marginLeft: 6, fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
                                  letterSpacing: '0.04em', opacity: 0.65
                                }}>
                                  current
                                </span>
                              )}
                            </span>
                          )}
                        </React.Fragment>
                      )
                    })}
                  </div>
                )
              })()}
              {renderDrawerRow('Aspire Drive (12 mo)', `${formatNumber(selectedProperty?.avgAspireDriveManHoursPerVisit)} mh avg · ${formatNumber(selectedProperty?.aspireDriveManHours12mo, 1)} total`)}
              <Typography variant="caption" style={{ opacity: 0.6, display: 'block', marginTop: -4 }}>
                Aspire-recorded drive — last stop of day absorbs route drive; informational only
              </Typography>
            </div>

            <Divider style={{ margin: '16px 0' }} />

            <div className={classes.drawerSection}>
              <Typography className={classes.drawerSectionTitle}>Spend Breakdown</Typography>
              {renderDrawerRow('Labor Cost', formatCurrency(selectedProperty?.totalLaborCost))}
              {renderDrawerRow('Material Cost', formatCurrency(selectedProperty?.totalMaterialCost))}
              {renderDrawerRow('Equipment Cost', formatCurrency(selectedProperty?.totalEquipmentCost))}
              {renderDrawerRow('Subcontractor Cost', formatCurrency(selectedProperty?.totalSubCost))}
              <Divider style={{ margin: '8px 0', opacity: 0.5 }} />
              {renderDrawerRow('Total Actual Spend', formatCurrency(selectedProperty?.totalActualSpend || (
                (selectedProperty?.totalLaborCost || 0) + 
                (selectedProperty?.totalMaterialCost || 0) + 
                (selectedProperty?.totalEquipmentCost || 0) + 
                (selectedProperty?.totalSubCost || 0)
              )), { fontWeight: 700 })}
            </div>

            <Divider style={{ margin: '16px 0' }} />

            <div className={classes.drawerSection}>
              <Typography className={classes.drawerSectionTitle}>Financials</Typography>
              {renderDrawerRow('Estimated Revenue', formatCurrency(selectedProperty?.totalEstimatedRevenue))}
              {renderDrawerRow('Est. Rev (Per Visit)', formatCurrency(selectedProperty?.contractPricePerVisit))}
              {renderDrawerRow('Earned Revenue', formatCurrency(selectedProperty?.totalEarnedRevenue))}
              {renderDrawerRow('Estimated Labor Cost', formatCurrency(selectedProperty?.estimatedLaborCost))}
              {renderDrawerRow('Actual Labor Cost', formatCurrency(selectedProperty?.totalLaborCost))}
              {renderDrawerRow('Cost Variance', formatCurrency(selectedProperty?.costVariance), getValueClass(selectedProperty?.costVariance))}
              {renderDrawerRow('Net Profit', formatCurrency(selectedProperty?.netProfit), getValueClass(selectedProperty?.netProfit))}
              {renderDrawerRow('Margin', formatPercent(selectedProperty?.profitMargin), getValueClass(selectedProperty?.profitMargin))}
            </div>

            <Divider style={{ margin: '16px 0' }} />

            <div className={classes.drawerSection}>
              <Typography className={classes.drawerSectionTitle}>Schedule</Typography>
              {renderDrawerRow('Primary Crew', getCrewLabel(selectedProperty?.primaryCrewLeader))}
              {selectedProperty?.scheduledDays?.length > 0 && (
                <Box display="flex" gridGap={6} flexWrap="wrap" mt={1} mb={1}>
                  {selectedProperty.scheduledDays.map(day => (
                    <Chip key={day} label={day} size="small" variant="outlined" />
                  ))}
                </Box>
              )}
              {selectedProperty?.assignedCrewLeaders?.length > 0 && (
                <Box display="flex" gridGap={6} flexWrap="wrap" mt={1}>
                  {selectedProperty.assignedCrewLeaders.map(crew => (
                    <Chip key={crew.id || crew.name} label={getCrewLabel(crew)} size="small" />
                  ))}
                </Box>
              )}
            </div>

            {selectedProperty?.futureVisits?.length > 0 && (
              <>
                <Divider style={{ margin: '16px 0' }} />
                <div className={classes.drawerSection}>
                  <Typography className={classes.drawerSectionTitle}>Upcoming Visits</Typography>
                  {selectedProperty.futureVisits.slice(0, 6).map((visit, index) => (
                    <div key={`${visit.workTicketId || visit.workTicketNumber || index}`} className={classes.visitRow}>
                      <Box display="flex" justifyContent="space-between" gridGap={12}>
                        <strong>{formatShortDate(visit.date)}</strong>
                        <span>{formatNumber(visit.estimatedHours)} hrs</span>
                      </Box>
                      <div className={classes.visitMeta}>
                        {[visit.dayOfWeek, visit.crewLeaderName, visit.statusName, renderTicketLink(visit)]
                          .filter(Boolean)
                          .map((item, itemIndex) => (
                            <React.Fragment key={itemIndex}>
                              {itemIndex > 0 && ' | '}
                              {item}
                            </React.Fragment>
                          ))}
                      </div>
                    </div>
                  ))}
                  {selectedProperty.futureVisits.length > 6 && (
                    <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 8 }}>
                      Showing 6 of {selectedProperty.futureVisits.length} future visits.
                    </Typography>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </Drawer>

      {/* Snackbar for sync notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  )
}

function RoutingPropertiesTable(props) {
  return (
    <ThemeProvider theme={desertFloraTheme}>
      <TerritoryProvider url={props.url}>
        <RoutingPropertiesTableInner {...props} />
      </TerritoryProvider>
    </ThemeProvider>
  )
}

const mapStateToProps = (state) => ({
  url: state.url,
  authenticated: state.authenticated
})

export {
  PropertyDetailDrawer,
  buildGeoInsights,
  buildSidebarData,
  getDayAbbrev,
  normalizeRoutingRow,
  useStyles as useRoutingPropertyDetailStyles
}

export default connect(mapStateToProps)(RoutingPropertiesTable)
